import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  applyOpacityToHtmlColor,
  parseColorToTArgb,
  parseTArgbToHtmlColor,
  NumericAxis,
  NumberRange,
  NumericLabelProvider,
  EAxisAlignment,
  EAutoRange,
  ECoordinateMode,
  XyDataSeries,
  XyScatterRenderableSeries,
  EllipsePointMarker,
  SquarePointMarker,
  TrianglePointMarker,
  XPointMarker,
  SpritePointMarker,
  type BasePointMarker,
  XyyDataSeries,
  FastBandRenderableSeries,
  FastLineRenderableSeries,
  VerticalLineAnnotation,
  HorizontalLineAnnotation,
  LineAnnotation,
  BoxAnnotation,
  ELabelPlacement,
  NativeTextAnnotation,
  TextAnnotation,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  MouseWheelZoomModifier,
  ZoomPanModifier,
  XAxisDragModifier,
  YAxisDragModifier,
  CursorModifier,
  ChartModifierBase2D,
  type ModifierMouseArgs,
  type IPointMetadata,
  type HitTestInfo,
  type IRenderableSeries,
  type SciChartSurface,
  type TSciChart,
} from 'scichart';
import {
  SciChartView,
  type SciChartInitContext,
} from 'components/charts/scichart';
import { formatNumber } from 'utils/number';
import { escape as escapeHtml } from 'lodash-es';
import type {
  GunBarrelSubject,
  GunBarrelFormationHorizontals,
  GunBarrelWellPoint,
  GunBarrelSlotItem,
  GunBarrelEditTargetKind,
  GunBarrelPositionEdit,
  GunBarrelEditAxis,
  GunBarrelSelectionInfo,
  GunBarrelChartHandle,
  FormationTvdGrid,
} from './types';
import { resolveSlotTvd, resolveSurfaceElevation } from './depth-basis';
import { WELL_STATUS_COLOR_MAP } from 'utils/well-status';
import {
  SLOT_CATEGORY_LABELS,
  WELL_STATUS_SLOT_CATEGORY_KEYS,
} from 'utils/slot-categories';
import classNames from 'classnames';
import styles from './gun-barrel-chart.module.scss';

// Diamond, dotted diamond and slashed circle have no SciChart equivalent, so they
// are drawn as sprites.
//
// SciChart pads the sprite canvas to PIXEL_RATIO * (size + strokeThickness) + 1 and
// then blits that whole canvas centred on the data point, so a sprite has to be drawn
// around the CANVAS centre. Centring on the width and height passed in leaves the shape
// up and to the left of the point by half the padding — invisible on its own, obvious
// once the marker sits inside a slot square.
const spriteCentre = (ctx: CanvasRenderingContext2D) => ({
  cx: ctx.canvas.width / 2,
  cy: ctx.canvas.height / 2,
});

const diamondPath = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number
) => {
  ctx.beginPath();
  ctx.moveTo(cx, cy - radius);
  ctx.lineTo(cx + radius, cy);
  ctx.lineTo(cx, cy + radius);
  ctx.lineTo(cx - radius, cy);
  ctx.closePath();
};

// Permit.
class DiamondPointMarker extends SpritePointMarker {
  override drawSprite(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    stroke: string,
    strokeThickness: number,
    fill: string
  ) {
    const { cx, cy } = spriteCentre(ctx);
    diamondPath(ctx, cx, cy, Math.min(w, h) / 2 - strokeThickness);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeThickness;
    ctx.stroke();
  }
}

/**
 * Dots along each edge of the expired-permit diamond, corners included. Three rather
 * than four: the dots have to stay clear of one another or the outline closes up and
 * the symbol reads as a plain diamond, which is the live permit's.
 */
const DIAMOND_DOTS_PER_EDGE = 3;

/**
 * The dot's radius, as a share of the stroke width. Bounded by the gap between dots —
 * at much above this they touch and the outline goes solid.
 */
const DOT_RADIUS_SHARE = 0.6;

/**
 * Extra darkening for the expired permit's dots, on top of the stroke's own. A well
 * takes its colour from the formation band behind it, so at the standard darkening the
 * dots sit a shade off their own background and all but disappear.
 */
const EXPIRED_PERMIT_DARKEN = 0.8;

/**
 * Expired permit: the permit's diamond, struck out as a dotted outline.
 *
 * The dots are placed one by one rather than stroked with a dash pattern. A dash runs
 * continuously around the path, so unless the perimeter divides exactly by the dash
 * period the run closes short and one edge ends up barer than the other three.
 */
class DottedDiamondPointMarker extends SpritePointMarker {
  override drawSprite(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    stroke: string,
    strokeThickness: number
  ) {
    const { cx, cy } = spriteCentre(ctx);
    const radius = Math.min(w, h) / 2 - strokeThickness;
    const corners = [
      [cx, cy - radius],
      [cx + radius, cy],
      [cx, cy + radius],
      [cx - radius, cy],
    ];
    const dotRadius = strokeThickness * DOT_RADIUS_SHARE;
    ctx.fillStyle = stroke;
    corners.forEach(([x0, y0], i) => {
      const [x1, y1] = corners[(i + 1) % corners.length]!;
      for (let step = 0; step < DIAMOND_DOTS_PER_EDGE; step++) {
        const t = step / DIAMOND_DOTS_PER_EDGE;
        ctx.beginPath();
        ctx.arc(
          x0! + (x1! - x0!) * t,
          y0! + (y1! - y0!) * t,
          dotRadius,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
    });
  }
}

// Shut-in. Slashed bottom-left to top-right, the way the legend slashes it.
class SlashedCirclePointMarker extends SpritePointMarker {
  override drawSprite(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    stroke: string,
    strokeThickness: number
  ) {
    const { cx, cy } = spriteCentre(ctx);
    // Inset by half the stroke, not a whole one, so the outer edge lands where the
    // producing circle's does and the two read as the same size.
    const radius = Math.min(w, h) / 2 - strokeThickness / 2;
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeThickness;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();
    const arm = radius * Math.SQRT1_2;
    ctx.beginPath();
    ctx.moveTo(cx - arm, cy + arm);
    ctx.lineTo(cx + arm, cy - arm);
    ctx.stroke();
  }
}

// The four Create New Well statuses (INS-13300): a hollow hexagon, pointy-top, the same size as
// the producing circle beside it. One shape for all four; their fill colours tell them apart.
class HollowHexagonPointMarker extends SpritePointMarker {
  /**
   * Strokes the hexagon; the stroke thickness is the marker's own.
   * @param {CanvasRenderingContext2D} ctx - The sprite canvas.
   * @param {number} w - Sprite width.
   * @param {number} h - Sprite height.
   * @param {string} stroke - Stroke colour.
   */
  override drawSprite(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    stroke: string
  ) {
    const { strokeThickness } = this;
    const { cx, cy } = spriteCentre(ctx);
    const radius = Math.min(w, h) / 2 - strokeThickness / 2;
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeThickness;
    ctx.beginPath();
    for (let corner = 0; corner < 6; corner++) {
      const angle = (corner * Math.PI) / 3 - Math.PI / 2;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      if (corner === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.closePath();
    ctx.stroke();
  }
}

/**
 * The four Create New Well statuses as the chart keys statuses (upper case), in display order.
 * Each has its own legend entry and toggle (INS-13300).
 */
const PRE_PERMIT_STATUS_KEYS = WELL_STATUS_SLOT_CATEGORY_KEYS.map((status) =>
  status.toUpperCase()
);

type MarkerFactory = (
  ctx: TSciChart,
  size: number,
  fill: string,
  stroke: string
) => BasePointMarker;

// Maps normalized well status → SciChart marker factory.
// Only used when the `statusMarkers` prop is true.
const STATUS_MARKER_FACTORY: Record<string, MarkerFactory> = {
  PRODUCING: (ctx, s, fill, stroke) =>
    new EllipsePointMarker(ctx, {
      width: s,
      height: s,
      fill,
      stroke,
      strokeThickness: 1,
    }),
  DUC: (ctx, s, fill, stroke) =>
    new TrianglePointMarker(ctx, {
      width: s,
      height: s,
      fill,
      stroke,
      strokeThickness: 1,
    }),
  PERMIT: (ctx, s, fill, stroke) =>
    new DiamondPointMarker(ctx, {
      width: s,
      height: s,
      fill,
      stroke,
      strokeThickness: 1,
    }),
  EXPIRED_PERMIT: (ctx, s, fill, stroke) =>
    new DottedDiamondPointMarker(ctx, {
      width: s,
      height: s,
      fill,
      stroke: darken(stroke, EXPIRED_PERMIT_DARKEN),
      strokeThickness: 1.5,
    }),
  SHUT_IN: (ctx, s, fill, stroke) =>
    new SlashedCirclePointMarker(ctx, {
      width: s,
      height: s,
      fill,
      stroke,
      strokeThickness: 1.5,
    }),
  INACTIVE: (ctx, s, fill, stroke) =>
    new XPointMarker(ctx, {
      width: s,
      height: s,
      fill,
      stroke,
      strokeThickness: 2,
    }),
  // A hollow shape: stroked in the well's own colour, as the hollow LTD square is.
  ...Object.fromEntries(
    PRE_PERMIT_STATUS_KEYS.map((key): [string, MarkerFactory] => [
      key,
      (ctx, s, fill) =>
        new HollowHexagonPointMarker(ctx, {
          width: s,
          height: s,
          fill,
          stroke: fill,
          strokeThickness: 2,
        }),
    ])
  ),
};

// Extra px added to the base marker size for statuses whose shape fills less of
// its box than the circle beside it. A diamond covers half its box, so a permit
// needs the most; a dotted outline needs the perimeter as well, to have room for
// enough dashes to read as dotted rather than as a broken line.
const STATUS_MARKER_SIZE_BONUS: Partial<Record<string, number>> = {
  PERMIT: 4,
  EXPIRED_PERMIT: 4,
};

const STATUS_LABEL: Record<string, string> = {
  PRODUCING: 'Producing',
  DUC: 'DUC',
  PERMIT: 'Permit',
  EXPIRED_PERMIT: 'Expired Permit',
  SHUT_IN: 'Shut-in',
  INACTIVE: 'Inactive',
};

// Maps status key → CSS module class name for the legend icon shape.
const STATUS_ICON_CLASS: Record<string, string> = {
  PRODUCING: 'legendCircle',
  DUC: 'legendTriangle',
  PERMIT: 'legendDiamond',
  EXPIRED_PERMIT: 'legendDiamondDotted',
  SHUT_IN: 'legendCircleSlash',
  INACTIVE: 'legendX',
};

// Default slice (eval-point row) rendered. A slice slider can drive this later.
const SLICE_INDEX = 0;
// A slot and the well crossing it represents come from the same backend
// computation, so their x agree to the foot; this only absorbs float rounding.
const SAME_CROSSING_FT = 1;
const SPACING_LOWER_LIMIT_FT = 200;
const SPACING_UPPER_LIMIT_FT = 5280;
const WELL_SERIES_ID = 'gb-well';
// Marker diameter (px) for well + slot circles. The hover hit-test radius is
// half this (the marker's visual radius) so the tooltip only fires when the
// cursor is actually over the dot, not in a halo around it.
const MARKER_SIZE_PX = 12;
// Band fill opacity: strong inside the unit, roughly a third of that outside.
const BAND_ALPHA_INSIDE = 0.6;
const BAND_ALPHA_OUTSIDE = 0.2;
// Formation bands extend far past the unit (edge values held constant) so the
// colors always reach both chart edges no matter how far the user pans —
// matching the mock. Only the in-unit segment renders at full opacity.
const BAND_X_EXTENT_FT = 50000;
// Each type-curve fill band runs from its top down to the next formation top,
// but never thicker than this. Without a derived base the fills used a fixed
// thickness that overlapped neighbors and produced inconsistent assignments.
const MAX_FORMATION_THICKNESS_FT = 2000;
const SPACING_LINE_COLOR = '#0f62fe';
const UNIT_BOUNDARY_COLOR = '#007d79';
// Cross-line setback keep-out band — Carbon orange-40 at ~30% (#ff832b4d).
//
// The mock's ~15% works because its bands are near-white; ours are saturated
// (formation fills run 45-60%), so that little orange over gold or blue was too
// dull to read.
const SETBACK_BAND_FILL = '#ff832b4d';
const TVD_AXIS_ID = 'tvd';

// The X range a property may occupy: the unit boundaries pulled in by the
// cross-line setback on each side (the keep-out bands are no-go), clamped so the
// two setbacks never cross. Placement/drag/nudge all clamp X to this span.
const drainableSpan = (
  ub: { leftXFt: number; rightXFt: number },
  setbackFt: number | null | undefined
): { leftXFt: number; rightXFt: number } => {
  const setback = setbackFt && setbackFt > 0 ? setbackFt : 0;
  const b = Math.min(setback, (ub.rightXFt - ub.leftXFt) / 2);
  return { leftXFt: ub.leftXFt + b, rightXFt: ub.rightXFt - b };
};
const TVDSS_AXIS_ID = 'tvdss';
// Edit mode: marker positions snap to this grid (ft) on both axes while
// dragging.
const DRAG_SNAP_FT = 5;
// Press-release within this pixel distance is a click (selection toggle),
// beyond it a drag.
const CLICK_MOVE_TOLERANCE_PX = 3;
// Series id prefixes that can initiate a grab in edit mode. The selection
// ring overlay is grabbable too — it is the biggest target on a selected
// marker.
const DRAG_GRAB_PREFIXES = [
  'gb-slot-rect-',
  'gb-slot-well-',
  'gb-well-',
  'gb-selected-ring',
];
// Permit movement is decided per slot by the backend (`landingEditable` on the
// inventory_unit_slots layer): a permit / pre-drill slot this workspace owns,
// cut from a planned trajectory that names its bore, moves in TVD with its X
// frozen; any other permit stays put. See `clampEdit` and `editableIds`.

// Series id prefixes whose points follow a dragged entity (grab set + filter
// halos).
const DRAG_FOLLOW_PREFIXES = [...DRAG_GRAB_PREFIXES, 'gb-halo-'];

// Edit-map keys are `slot:{slotId}` / `uwi:{uwi}` — parse one back into its
// entity identity.
const parseEditKey = (
  key: string
): {
  kind: GunBarrelEditTargetKind;
  slotId: number | null;
  uwi: string | null;
} => {
  return key.startsWith('slot:')
    ? { kind: 'slot', slotId: Number(key.slice('slot:'.length)), uwi: null }
    : { kind: 'well', slotId: null, uwi: key.slice('uwi:'.length) };
};

// The three movement groups, keyed off `geometrySourceType`. LTD is clamped
// inside its formation; a permit / DUC / PDP without survey geometry moves in
// TVD only (X frozen), free across formations; a survey (drilled) well can't be
// edited at all. A slot with a well but no survey geometry is a permit.
type GunBarrelItemType = 'ltd' | 'survey' | 'permit';
/**
 * The generator's orphan bucket: a well it slotted but could not land. Not a
 * formation, so it never has a band. Twin of pages/inventory-workspace/tree-leaves'
 * isUnknownZone (shared code does not import from pages/).
 * @param {string} formation - A slot's formation label.
 * @return {boolean} Whether the label is the UNKNOWN bucket.
 */
const isUnknownZone = (formation: string): boolean =>
  formation.trim().toUpperCase() === 'UNKNOWN';
const classifySlot = (s: GunBarrelSlotItem): GunBarrelItemType => {
  if (s.geometrySourceType === 'survey_geometry') return 'survey';
  if (s.geometrySourceType === 'ltd') return 'ltd';
  return s.hasWell ? 'permit' : 'ltd';
};
const AXIS_LABEL_STYLE = { fontSize: 10, color: '#393939' };
const AXIS_TITLE_STYLE = { fontSize: 11, color: '#393939' };

const fmtFt = (v: number) => formatNumber(v, 0, 0);

// Stable defaults for the data props, so effects keyed on them do not fire on
// every render when a caller passes none.
const NO_WELLS: GunBarrelWellPoint[] = [];
const NO_SLOTS: GunBarrelSlotItem[] = [];

// A well moved in depth whose landing analysis has not reported yet (Story 1):
// drawn held — its circle faded to this opacity, not editable — until the
// workspace channel says where it landed and the chart refetches.
const LANDING_PENDING_TOOLTIP =
  '<i>Landing analysis running — formation and position update when it finishes</i>';
const LANDING_PENDING_ALPHA = 0.3;

// Color helpers built on SciChart's native color utilities (no external lib).
// `withAlpha` overlays a fill opacity; `darken` shades a marker stroke toward
// black by scaling its RGB channels.
const STROKE_DARKEN_FACTOR = 0.7;
const withAlpha = (color: string, alpha: number): string => {
  try {
    return applyOpacityToHtmlColor(color, alpha);
  } catch {
    return color;
  }
};
const darken = (
  color: string,
  factor: number = STROKE_DARKEN_FACTOR
): string => {
  try {
    const { opacity, red, green, blue } = parseColorToTArgb(color);
    return parseTArgbToHtmlColor({
      opacity,
      red: Math.round(red * factor),
      green: Math.round(green * factor),
      blue: Math.round(blue * factor),
    });
  } catch {
    return color;
  }
};

// A slot item once the draw pass has derived its TVD on the section datum.
type SlotWithTvd = GunBarrelSlotItem & {
  tvdFt: number;
  backendTvdFt: number | null;
  midY: number;
};

interface SlotWellMarkersArgs {
  surface: SciChartSurface;
  wasmContext: TSciChart;
  /** One colour group of the drawn slots (the slot squares' band colour). */
  group: SlotWithTvd[];
  colorKey: string | null;
  /** The group's index, which keys the series ids. */
  slotIdx: number;
  statusMarkers: boolean;
  hiddenStatuses: ReadonlySet<string>;
  slotEdit: (slotId: number) => GunBarrelPositionEdit | undefined;
  surfaceElev: number;
}

/**
 * The circle inside each well-backed slot's square — one series per status
 * group in statusMarkers mode (each status has its own shape), one circle
 * series otherwise. A well whose landing is still running (Story 1) draws in a
 * faded series of its own, so the rest of its group keeps its full colour.
 *
 * @param {SlotWellMarkersArgs} args - The surface to draw on and the group to draw.
 */
const addSlotWellMarkers = ({
  surface,
  wasmContext,
  group,
  colorKey,
  slotIdx,
  statusMarkers,
  hiddenStatuses,
  slotEdit,
  surfaceElev,
}: SlotWellMarkersArgs) => {
  const wellSlots = group.filter(
    (s) =>
      s.hasWell &&
      !(
        statusMarkers &&
        s.wellStatus &&
        hiddenStatuses.has(s.wellStatus.toUpperCase())
      )
  );
  if (wellSlots.length === 0) return;

  const fill = colorKey ?? '#ffffff';
  const stroke = colorKey ? darken(colorKey) : '#161616';
  // In statusMarkers mode, group slot-wells by wellStatus so each gets the
  // right shape. Outside that mode, all use a circle.
  const byStatus = new Map<string | null, SlotWithTvd[]>();
  wellSlots.forEach((s) => {
    const k = statusMarkers ? (s.wellStatus ?? '').toUpperCase() || null : null;
    byStatus.set(k, [...(byStatus.get(k) ?? []), s]);
  });
  const series = [...byStatus.entries()].flatMap(([normStatus, items]) =>
    [
      {
        normStatus,
        held: false,
        items: items.filter((s) => !s.landingPending),
      },
      { normStatus, held: true, items: items.filter((s) => s.landingPending) },
    ].filter((g) => g.items.length > 0)
  );

  series.forEach(({ normStatus, held, items }, groupIdx) => {
    // The well inside an occupied slot sits within the slot's own square
    // (MARKER_SIZE_PX + 4), so it is capped at the base size: a shape carrying
    // a size bonus would otherwise reach the square's edges and read as
    // mis-centred rather than nested inside it.
    const slotMarkerSize = Math.min(
      MARKER_SIZE_PX - 2 + (STATUS_MARKER_SIZE_BONUS[normStatus ?? ''] ?? 0),
      MARKER_SIZE_PX
    );
    const groupFill = held ? withAlpha(fill, LANDING_PENDING_ALPHA) : fill;
    const groupStroke = held
      ? withAlpha(stroke, LANDING_PENDING_ALPHA)
      : stroke;
    const pointMarker =
      statusMarkers && normStatus
        ? (
            STATUS_MARKER_FACTORY[normStatus] ??
            STATUS_MARKER_FACTORY['PRODUCING']
          )(wasmContext, slotMarkerSize, groupFill, groupStroke)
        : new EllipsePointMarker(wasmContext, {
            width: MARKER_SIZE_PX - 2,
            height: MARKER_SIZE_PX - 2,
            fill: groupFill,
            stroke: groupStroke,
            strokeThickness: 1,
          });
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasmContext, {
        id: `gb-slot-well-${slotIdx}-${groupIdx}`,
        yAxisId: TVD_AXIS_ID,
        dataSeries: new XyDataSeries(wasmContext, {
          xValues: items.map(
            (s) => s.xOffsetFt + (slotEdit(s.slotId)?.dxFt ?? 0)
          ),
          yValues: items.map(
            (s) => surfaceElev - s.midY + (slotEdit(s.slotId)?.dTvdFt ?? 0)
          ),
          // slotId makes grabbing the inner marker resolve to the slot — an
          // occupied slot moves as one unit.
          metadata: items.map((s) => ({
            isSelected: false,
            kind: 'well' as const,
            label: s.uwi ?? '',
            tvdFt: s.tvdFt + (slotEdit(s.slotId)?.dTvdFt ?? 0),
            uwi: s.uwi ?? '',
            wellName: s.wellName ?? null,
            status: s.wellStatus ?? '',
            slotId: s.slotId,
            landingPending: held,
          })),
        }),
        pointMarker,
      })
    );
  });
};

const rowAt = (
  grid: FormationTvdGrid | null,
  idx: number
): (number | null)[] => {
  if (!grid || grid.length === 0) return [];
  return grid[Math.min(Math.max(idx, 0), grid.length - 1)] ?? [];
};

// Mean of the non-null entries; 0 when the row has no defined values.
const avgDefined = (values: (number | null)[]): number => {
  let sum = 0;
  let count = 0;
  for (const v of values) {
    if (v !== null) {
      sum += v;
      count += 1;
    }
  }
  return count > 0 ? sum / count : 0;
};

// Clip an [x, top, base] band to [xLo, xHi], interpolating at the cut points so
// the inside/outside-unit segments meet exactly at the boundary lines.
function clipBand(
  xs: number[],
  tops: number[],
  bases: number[],
  xLo: number,
  xHi: number
): { xs: number[]; tops: number[]; bases: number[] } {
  const oX: number[] = [];
  const oT: number[] = [];
  const oB: number[] = [];
  for (let i = 0; i < xs.length - 1; i++) {
    const x0 = xs[i];
    const x1 = xs[i + 1];
    if (x1 < xLo || x0 > xHi) continue;
    let ax = x0;
    let at = tops[i];
    let ab = bases[i];
    let bx = x1;
    let bt = tops[i + 1];
    let bb = bases[i + 1];
    if (x0 < xLo) {
      const u = (xLo - x0) / (x1 - x0);
      ax = xLo;
      at = tops[i] + u * (tops[i + 1] - tops[i]);
      ab = bases[i] + u * (bases[i + 1] - bases[i]);
    }
    if (x1 > xHi) {
      const u = (xHi - x0) / (x1 - x0);
      bx = xHi;
      bt = tops[i] + u * (tops[i + 1] - tops[i]);
      bb = bases[i] + u * (bases[i + 1] - bases[i]);
    }
    if (oX.length === 0 || oX[oX.length - 1] !== ax) {
      oX.push(ax);
      oT.push(at);
      oB.push(ab);
    }
    oX.push(bx);
    oT.push(bt);
    oB.push(bb);
  }
  return { xs: oX, tops: oT, bases: oB };
}

// Project a formation's top/base rows (at the slice) into a band that spans
// [xMin, xMax], holding the edge values constant beyond the sampled positions.
function buildBand(
  slicePositions: number[],
  topRow: (number | null)[],
  baseRow: (number | null)[],
  xMin: number,
  xMax: number
): { xs: number[]; tops: number[]; bases: number[]; midY: number } | null {
  const xs: number[] = [];
  const tops: number[] = [];
  const bases: number[] = [];
  const n = Math.min(slicePositions.length, topRow.length, baseRow.length);
  for (let i = 0; i < n; i++) {
    const t = topRow[i];
    const b = baseRow[i];
    if (t === null || b === null) continue;
    xs.push(slicePositions[i]);
    tops.push(t);
    bases.push(b);
  }
  if (xs.length < 2) return null;
  if (xs[0] > xMin) {
    xs.unshift(xMin);
    tops.unshift(tops[0]);
    bases.unshift(bases[0]);
  }
  if (xs[xs.length - 1] < xMax) {
    xs.push(xMax);
    tops.push(tops[tops.length - 1]);
    bases.push(bases[bases.length - 1]);
  }
  let sum = 0;
  for (let i = 0; i < tops.length; i++) sum += (tops[i] + bases[i]) / 2;
  return { xs, tops, bases, midY: sum / tops.length };
}

// Linearly interpolate a piecewise-linear series (xs→ys) at a given x.
// Clamps to the first/last value when x is outside the sampled range.
function interpolateAtX(xs: number[], ys: number[], x: number): number {
  if (xs.length === 0) return 0;
  if (x <= xs[0]) return ys[0];
  if (x >= xs[xs.length - 1]) return ys[ys.length - 1];
  for (let i = 0; i < xs.length - 1; i++) {
    if (x >= xs[i] && x <= xs[i + 1]) {
      const t = (x - xs[i]) / (xs[i + 1] - xs[i]);
      return ys[i] + t * (ys[i + 1] - ys[i]);
    }
  }
  return ys[ys.length - 1];
}

const DASH = '—';

const fmtMaybe = (v: number | null | undefined): string => {
  return v === null || v === undefined ? DASH : formatNumber(v, 0, 0);
};

// Per-marker metadata used to build the hover tooltip.
interface MarkerMeta extends IPointMetadata {
  kind: 'well' | 'location' | 'slot' | 'typeCurve';
  /** UWI (wells) or TC formation name (slot locations). */
  label: string;
  tvdFt: number;
  /** Backend TVDSS for direct display in tooltip. */
  tvdssFt?: number;
  /** Whether the slot has a UWI — drives "Avg" prefix in tooltip. */
  hasUwi?: boolean;
  /** Wells only — shown in the tooltip. */
  uwi?: string;
  /** "LEASE #NUMBER", shown under the UWI in the tooltip. */
  wellName?: string | null;
  status?: string;
  typeCurveFormation?: string;
  slotStatus?: string;
  wellStatus?: string;
  /** Slot items only — formation_slot id for click handling. */
  slotId?: number;
  /** Slot items only — whether this slot is valued. */
  valued?: boolean;
  /** A depth move of this well whose landing analysis is still running. */
  landingPending?: boolean;
  typeCurveHeaderDetailsId?: number | null;
  tcPublicId?: string | null;
  uniqueTcId?: string | null;
  oilEurMbbl30yr?: number | null;
  gasEurMmcf30yr?: number | null;
  waterEurMbbl30yr?: number | null;
}

/**
 * The vertical range (in TVD values) within the gun barrel plot where the user
 * can right-click to reveal the type curve's tooltip. The ranges of each type
 * curve do not overlap.
 */
interface TypeCurveHitBand {
  meta: MarkerMeta;
  yMinTvd: number;
  yMaxTvd: number;
}

/**
 * The tooltip's title rows for a well-backed marker: the UWI, in bold and
 * linked to the well page, with the well's name on the line under it. The name
 * is operator-entered text, so it is escaped; the UWI is digits.
 * @param {MarkerMeta} meta - The hovered marker.
 * @return {string[]} The title rows, empty for a marker with no well.
 */
const wellTitleRows = (meta: MarkerMeta): string[] => {
  if (!meta.uwi) return [];
  const link =
    `<a href="/wells/${meta.uwi}" target="_blank" rel="noopener noreferrer">` +
    `${meta.uwi}</a>`;
  return [
    `<b>${link}</b>`,
    ...(meta.wellName ? [escapeHtml(meta.wellName)] : []),
  ];
};

/**
 * Series the hover tooltip reads. Halo + selection overlays carry metadata for
 * edit-mode dragging, not tooltips; type-curve links have their own modifier.
 * @param {IRenderableSeries} series - A series on the surface.
 * @return {boolean} Whether the tooltip may hit-test it.
 */
const hoverableSeries = (series: IRenderableSeries): boolean =>
  series.isVisible &&
  series.id.startsWith('gb-') &&
  !series.id.startsWith('gb-tc-link-') &&
  !series.id.startsWith('gb-halo-') &&
  !series.id.startsWith('gb-selected-');

/**
 * Whether a hit lies within `radius` pixels of the cursor (a real hit on the
 * marker, not the nearest-by-X match scatter series also report).
 * @param {HitTestInfo} ht - The hit test result.
 * @param {number} radius - The marker radius in pixels.
 * @return {boolean} Whether the cursor is over the marker.
 */
const hitWithin = (ht: HitTestInfo, radius: number): boolean =>
  ht.isHit &&
  ht.dataSeriesIndex >= 0 &&
  Number.isFinite(ht.distance) &&
  ht.distance <= radius;

/**
 * The status row shared by slot and location tooltips: the well's status when
 * the slot has a well, else the slot's own.
 * @param {MarkerMeta} meta - The marker's metadata.
 * @return {string[]} One row, or none.
 */
const statusRow = (meta: MarkerMeta): string[] => {
  const status = meta.wellStatus || meta.slotStatus;
  return status ? [`Status: ${status}`] : [];
};

/**
 * Tooltip rows for a standalone well marker.
 * @param {MarkerMeta} meta - The marker's metadata.
 * @return {string[]} The rows.
 */
const wellMarkerRows = (meta: MarkerMeta): string[] => [
  ...(meta.uwi ? wellTitleRows(meta) : [`<b>UWI:</b> ${DASH}`]),
  `<b>Status:</b> ${meta.status || DASH}`,
  `<b>Type Curve Formation:</b> ${meta.typeCurveFormation || DASH}`,
  ...(meta.landingPending ? [LANDING_PENDING_TOOLTIP] : []),
];

/**
 * Tooltip rows for a slot square (LTD or well-backed).
 * @param {MarkerMeta} meta - The marker's metadata.
 * @param {number} elevFt - The section's surface elevation, for TVDSS.
 * @return {string[]} The rows.
 */
const slotMarkerRows = (meta: MarkerMeta, elevFt: number): string[] => {
  const tvdLabel = meta.hasUwi ? 'TVD' : 'Avg TVD';
  const tvdssLabel = meta.hasUwi ? 'TVDSS' : 'Avg TVDSS';
  return [
    // A well-backed slot is titled by its well (linked UWI, name under it); an
    // LTD by its slot label.
    ...(meta.uwi ? wellTitleRows(meta) : [`<b>${meta.label}</b>`]),
    `${tvdLabel}: ${fmtFt(meta.tvdFt)} ft`,
    `${tvdssLabel}: ${fmtFt(meta.tvdssFt ?? elevFt - meta.tvdFt)} ft`,
    ...statusRow(meta),
    ...(meta.landingPending ? [LANDING_PENDING_TOOLTIP] : []),
  ];
};

// Hover tooltip (DOM div at the cursor) over any gun-barrel marker series —
// wells or type-curve slot-location circles. Repo SciChart house pattern.
// Shows VS-section depth (TVD + TVDSS) + spacing for slot locations.
//
// A double-click on a well-backed marker PINS the tooltip: it stays where it is,
// takes pointer events, and the UWI link can be clicked. Any mouse-down on the
// chart (not on the tooltip itself) unpins it; so does a double-click elsewhere.
class GunBarrelHoverModifier extends ChartModifierBase2D {
  readonly type = 'GunBarrelHoverModifier';
  private tooltipEl: HTMLDivElement | null = null;
  private containerEl: HTMLElement | null = null;
  private spacingFt = 0;
  private elevFt = 0;
  private pinned = false;
  // The marker the tooltip last showed. A double-click pins THIS one: in edit
  // mode the gesture's two clicks toggle the selection, which rebuilds the
  // series, and a hit test at the double-click itself can come back empty.
  private lastShown: MarkerMeta | null = null;
  private readonly hitRadius = MARKER_SIZE_PX / 2;

  setConfig(cfg: {
    tooltipEl: HTMLDivElement | null;
    spacingFt: number;
    elevFt: number;
  }) {
    this.tooltipEl = cfg.tooltipEl;
    this.containerEl = cfg.tooltipEl?.parentElement ?? null;
    this.spacingFt = cfg.spacingFt;
    this.elevFt = cfg.elevFt;
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    if (!this.tooltipEl || !this.parentSurface) return;
    // A pinned tooltip stays put until the chart is clicked.
    if (this.pinned) return;
    // An in-progress edit drag owns the mouse — no hover tooltip.
    if (args.handled) {
      this.tooltipEl.style.display = 'none';
      return;
    }
    const meta = this.metaAt(args);
    if (!meta) {
      this.lastShown = null;
      this.tooltipEl.style.display = 'none';
      return;
    }
    this.lastShown = meta;
    this.render(meta, args);
  }

  /**
   * Pin the tooltip of the well-backed marker under the cursor, or unpin if
   * there is none there. The two clicks of the gesture have already toggled the
   * selection twice, so the selection is as it was.
   * @param {ModifierMouseArgs} args - The double-click.
   */
  override modifierDoubleClick(args: ModifierMouseArgs): void {
    super.modifierDoubleClick(args);
    if (!this.tooltipEl || !this.parentSurface) return;
    const meta = this.metaAt(args) ?? this.lastShown;
    if (!meta?.uwi) {
      this.unpin();
      return;
    }
    this.pinned = true;
    this.tooltipEl.style.pointerEvents = 'auto';
    this.render(meta, args);
  }

  /**
   * A click on the chart releases a pinned tooltip. A click on the tooltip
   * itself (the link) never reaches the chart: the pinned tooltip takes pointer
   * events, so the canvas does not see it.
   * @param {ModifierMouseArgs} args - The mouse-down.
   */
  override modifierMouseDown(args: ModifierMouseArgs): void {
    super.modifierMouseDown(args);
    if (this.pinned) this.unpin();
  }

  /**
   * Tooltip rows for a type-curve slot-location circle.
   * @param {MarkerMeta} meta - The marker's metadata.
   * @return {string[]} The rows.
   */
  private locationRows(meta: MarkerMeta): string[] {
    return [
      `<b>${meta.label}</b>`,
      `TVD: ${fmtFt(meta.tvdFt)} ft`,
      `TVDSS: ${fmtFt(this.elevFt - meta.tvdFt)} ft`,
      ...statusRow(meta),
      ...(this.spacingFt > 0 ? [`Spacing: ${fmtFt(this.spacingFt)} ft`] : []),
    ];
  }

  /**
   * Release a pinned tooltip, if there is one. Called by the chart when its
   * data changes, so a tooltip pinned before a refetch (a landing resolving, a
   * unit switch) does not stay up with stale depths at a stale position.
   */
  releasePin() {
    if (this.pinned) this.unpin();
  }

  /** Release a pinned tooltip and hide it. */
  private unpin() {
    this.pinned = false;
    this.lastShown = null;
    if (!this.tooltipEl) return;
    this.tooltipEl.style.pointerEvents = '';
    this.tooltipEl.style.display = 'none';
  }

  /**
   * The marker under the cursor, if any: the first series whose hit is within
   * the marker's own radius.
   * @param {ModifierMouseArgs} args - The mouse event.
   * @return {MarkerMeta | null} Its metadata, or null off any marker.
   */
  private metaAt(args: ModifierMouseArgs): MarkerMeta | null {
    if (!this.parentSurface) return null;
    for (const series of this.parentSurface.renderableSeries.asArray()) {
      if (!hoverableSeries(series)) continue;
      const ht = series.hitTestProvider?.hitTest(
        args.mousePoint.x,
        args.mousePoint.y,
        this.hitRadius
      );
      // `isHit` alone is too permissive — for scatter series it reports a
      // nearest-by-X match even when the cursor is far above/below the marker.
      // Gate on true pixel distance so the tooltip only fires over the dot.
      if (!ht || !hitWithin(ht, this.hitRadius)) continue;
      const ds = series.dataSeries as XyDataSeries;
      const m = ds.getMetadataAt(ht.dataSeriesIndex) as MarkerMeta | null;
      if (m) return m;
    }
    return null;
  }

  /**
   * Fill and place the tooltip for a marker, next to the cursor.
   * @param {MarkerMeta} meta - The marker's metadata.
   * @param {ModifierMouseArgs} args - The mouse event to place it by.
   */
  private render(meta: MarkerMeta, args: ModifierMouseArgs) {
    if (!this.tooltipEl) return;
    const rows =
      meta.kind === 'well'
        ? wellMarkerRows(meta)
        : meta.kind === 'slot'
          ? slotMarkerRows(meta, this.elevFt)
          : this.locationRows(meta);
    this.tooltipEl.innerHTML = rows.join('<br/>');
    this.tooltipEl.style.display = 'block';
    if (this.containerEl) {
      const rect = this.containerEl.getBoundingClientRect();
      const e = args.nativeEvent as MouseEvent;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const tW = this.tooltipEl.offsetWidth;
      const tH = this.tooltipEl.offsetHeight;
      let left = mx + 12;
      let top = my - tH - 8;
      if (top < 0) top = my + 12;
      if (left + tW > rect.width) left = mx - tW - 12;
      this.tooltipEl.style.left = `${Math.max(0, left)}px`;
      this.tooltipEl.style.top = `${Math.max(
        0,
        Math.min(top, rect.height - tH)
      )}px`;
    }
  }

  override modifierMouseLeave(args: ModifierMouseArgs): void {
    super.modifierMouseLeave(args);
    if (this.pinned) return;
    if (this.tooltipEl) this.tooltipEl.style.display = 'none';
  }
}

// Right-click tooltip for type-curve labels. Keeps DOM pointer events enabled so
// the ID link can open the Type Curve detail page in a new tab.
class GunBarrelTypeCurveContextModifier extends ChartModifierBase2D {
  readonly type = 'GunBarrelTypeCurveContextModifier';
  private tooltipEl: HTMLDivElement | null = null;
  private containerEl: HTMLElement | null = null;
  private hitBands: TypeCurveHitBand[] = [];

  setConfig(cfg: {
    tooltipEl: HTMLDivElement | null;
    hitBands: TypeCurveHitBand[];
  }) {
    this.tooltipEl = cfg.tooltipEl;
    this.containerEl = cfg.tooltipEl?.parentElement ?? null;
    this.hitBands = cfg.hitBands;
  }

  override modifierMouseDown(args: ModifierMouseArgs): void {
    super.modifierMouseDown(args);
    const e = args.nativeEvent as MouseEvent;
    if (e.button !== 2) {
      this.hide();
      return;
    }

    const meta = this.hitTestTypeCurve(args);
    if (!meta) {
      this.hide();
      return;
    }

    e.preventDefault();
    this.show(meta, e);
  }

  private hitTestTypeCurve(args: ModifierMouseArgs): MarkerMeta | null {
    if (!this.parentSurface) return null;
    const yAxis = this.parentSurface.yAxes.getById(TVD_AXIS_ID);
    const yCalc = yAxis?.getCurrentCoordinateCalculator();
    if (!yCalc) return null;

    const tvd = yCalc.getDataValue(args.mousePoint.y);
    const band = this.hitBands.find(
      ({ yMinTvd, yMaxTvd }) => tvd >= yMinTvd && tvd < yMaxTvd
    );

    return band?.meta ?? null;
  }

  private show(meta: MarkerMeta, e: MouseEvent) {
    if (!this.tooltipEl || !meta.typeCurveHeaderDetailsId) return;

    this.tooltipEl.replaceChildren();
    const link = document.createElement('a');
    link.href = `/type_curves/${meta.tcPublicId}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = meta.uniqueTcId || meta.label;
    this.tooltipEl.appendChild(link);

    const rows = [
      ['Status', meta.status || DASH],
      ['Oil EUR', `${fmtMaybe(meta.oilEurMbbl30yr)} MBbl`],
      ['Gas EUR', `${fmtMaybe(meta.gasEurMmcf30yr)} MMcf`],
      ['Water EUR', `${fmtMaybe(meta.waterEurMbbl30yr)} MBbl`],
    ];
    rows.forEach(([label, value]) => {
      const row = document.createElement('div');
      row.textContent = `${label}: ${value}`;
      this.tooltipEl?.appendChild(row);
    });

    this.tooltipEl.style.display = 'block';
    this.position(e);
  }

  private position(e: MouseEvent) {
    if (!this.tooltipEl || !this.containerEl) return;

    const rect = this.containerEl.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const tW = this.tooltipEl.offsetWidth;
    const tH = this.tooltipEl.offsetHeight;
    let left = mx + 12;
    let top = my + 12;
    if (left + tW > rect.width) left = mx - tW - 12;
    if (top + tH > rect.height) top = my - tH - 12;
    this.tooltipEl.style.left = `${Math.max(0, left)}px`;
    this.tooltipEl.style.top = `${Math.max(0, top)}px`;
  }

  private hide() {
    if (this.tooltipEl) this.tooltipEl.style.display = 'none';
  }
}

// Click modifier for slot markers. Left-click on a `gb-slot-rect-*` marker fires
// `onSlotClick(slotId)` (on mouseUp, to avoid clashing with drag gestures);
// right-click on ANY slot marker (`gb-slot-*` — LTD squares and occupied-well
// glyphs) fires `onSlotRightClick(slotId)` on mouseDown, for the valuation flow.
class GunBarrelClickModifier extends ChartModifierBase2D {
  readonly type = 'GunBarrelClickModifier';
  private readonly hitRadius = MARKER_SIZE_PX / 2 + 4;
  private onSlotClick: ((slotId: number) => void) | null = null;
  private onSlotRightClick: ((slotId: number) => void) | null = null;

  setCallback(cb: ((slotId: number) => void) | null) {
    this.onSlotClick = cb;
  }

  setRightClickCallback(cb: ((slotId: number) => void) | null) {
    this.onSlotRightClick = cb;
  }

  // The slot id of the first marker under the point whose series id starts with
  // `prefix`, or null. Reads `MarkerMeta.slotId` from the hit point's metadata.
  private slotIdAt(x: number, y: number, prefix: string): number | null {
    if (!this.parentSurface) return null;
    for (const series of this.parentSurface.renderableSeries.asArray()) {
      if (!series.isVisible || !series.id.startsWith(prefix)) continue;
      const provider = series.hitTestProvider;
      if (!provider) continue;
      const ht = provider.hitTest(x, y, this.hitRadius);
      if (
        ht.isHit &&
        ht.dataSeriesIndex >= 0 &&
        Number.isFinite(ht.distance) &&
        ht.distance <= this.hitRadius
      ) {
        const ds = series.dataSeries as XyDataSeries;
        const m = ds.getMetadataAt(ht.dataSeriesIndex) as MarkerMeta | null;
        if (m?.slotId !== null && m?.slotId !== undefined) return m.slotId;
      }
    }
    return null;
  }

  override modifierMouseDown(args: ModifierMouseArgs): void {
    super.modifierMouseDown(args);
    if ((args.nativeEvent as MouseEvent).button !== 2) return;
    if (!this.onSlotRightClick) return;
    // Any slot marker, regardless of well type (LTD square or occupied well).
    const slotId = this.slotIdAt(
      args.mousePoint.x,
      args.mousePoint.y,
      'gb-slot-'
    );
    if (slotId === null) return;
    (args.nativeEvent as MouseEvent).preventDefault();
    this.onSlotRightClick(slotId);
  }

  override modifierMouseUp(args: ModifierMouseArgs): void {
    super.modifierMouseUp(args);
    // A completed edit drag marks its mouseup handled — not a click.
    if (args.handled) return;
    if (!this.onSlotClick) return;
    const slotId = this.slotIdAt(
      args.mousePoint.x,
      args.mousePoint.y,
      'gb-slot-rect-'
    );
    if (slotId !== null) this.onSlotClick(slotId);
  }
}

// What a completed drag gesture reports. Every dragged key shares the same
// gesture delta; the chart merges it into the cumulative per-entity edit map.
interface DragEndPayload {
  /** `slot:{slotId}` / `uwi:{uwi}` edit-map keys moved by this gesture. */
  keys: string[];
  dxFt: number;
  dTvdFt: number;
}

interface DragTarget {
  series: IRenderableSeries;
  index: number;
  baseX: number;
  baseY: number;
  /** Edit-map key this point belongs to (`slot:{id}` / `uwi:{uwi}`). */
  key: string;
}

// Edit-mode modifier implementing the select-then-drag flow:
// - Click on an editable marker toggles its editable (selected) state.
// - Click on empty space clears the selection.
// - ONLY selected markers are draggable — dragging one moves the WHOLE
//   selection. A drag attempt on an unselected marker is swallowed (no pan,
//   no move); panning stays available from empty chart space.
// Paired layers (occupied slot's inner marker, `−` glyph, filter halos,
// selection dots + TVD guide lines) travel with their entity. Reads live
// React state through refs because chart modifiers are created once in
// initChart.
class GunBarrelDragModifier extends ChartModifierBase2D {
  readonly type = 'GunBarrelDragModifier';
  private readonly hitRadius = MARKER_SIZE_PX / 2 + 4;
  private zoomPan: ZoomPanModifier | null = null;
  private cursor: CursorModifier | null = null;
  private editModeRef: { current: boolean } | null = null;
  private editableIdsRef: { current: ReadonlySet<string> } | null = null;
  // Selectable but never draggable (other formations' LTDs, for merging).
  private pickableIdsRef: { current: ReadonlySet<string> } | null = null;
  private selectedKeysRef: { current: ReadonlySet<string> } | null = null;
  private boundaries: { leftXFt: number; rightXFt: number } | null = null;
  private onDragEnd: ((p: DragEndPayload) => void) | null = null;
  private onSelectionChange: ((next: ReadonlySet<string>) => void) | null =
    null;
  // Per-well-type constraints supplied by the React layer (null = no restriction,
  // i.e. the plain Type Curve behaviour).
  private getXFrozen: ((key: string) => boolean) | null = null;
  private getTvdBounds:
    ((key: string, xFt: number) => { lo: number; hi: number } | null) | null =
    null;

  private dragging = false;
  // Press that can only become a click: empty space (clears selection) or an
  // unselected marker (selects it). Pan stays live during these presses.
  private clickPress = false;
  private clickPressKey: string | null = null;
  private movedPx = false;
  private startMouseX = 0;
  private startMouseY = 0;
  private pressKey = '';
  private dragKeys: string[] = [];
  private targets: DragTarget[] = [];
  private textTargets: { ann: TextAnnotation; baseX: number; baseY: number }[] =
    [];
  private lineTargets: { ann: HorizontalLineAnnotation; baseY: number }[] = [];
  private lastDx = 0;
  private lastDy = 0;

  setRefs(cfg: {
    zoomPan: ZoomPanModifier;
    cursor: CursorModifier;
    editModeRef: { current: boolean };
    editableIdsRef: { current: ReadonlySet<string> };
    pickableIdsRef?: { current: ReadonlySet<string> };
    selectedKeysRef: { current: ReadonlySet<string> };
  }) {
    this.zoomPan = cfg.zoomPan;
    this.cursor = cfg.cursor;
    this.editModeRef = cfg.editModeRef;
    this.editableIdsRef = cfg.editableIdsRef;
    this.pickableIdsRef = cfg.pickableIdsRef ?? null;
    this.selectedKeysRef = cfg.selectedKeysRef;
  }

  setConfig(cfg: {
    boundaries: { leftXFt: number; rightXFt: number } | null;
    onDragEnd: ((p: DragEndPayload) => void) | null;
    onSelectionChange: ((next: ReadonlySet<string>) => void) | null;
    getXFrozen?: (key: string) => boolean;
    getTvdBounds?: (
      key: string,
      xFt: number
    ) => { lo: number; hi: number } | null;
  }) {
    this.boundaries = cfg.boundaries;
    this.onDragEnd = cfg.onDragEnd;
    this.onSelectionChange = cfg.onSelectionChange;
    this.getXFrozen = cfg.getXFrozen ?? null;
    this.getTvdBounds = cfg.getTvdBounds ?? null;
  }

  override modifierMouseDown(args: ModifierMouseArgs): void {
    super.modifierMouseDown(args);
    if (!this.editModeRef?.current || !this.boundaries || !this.parentSurface)
      return;
    if ((args.nativeEvent as MouseEvent).button !== 0) return;
    const grab = this.hitTestGrabbable(args.mousePoint.x, args.mousePoint.y);
    const selected = this.selectedKeysRef?.current ?? new Set<string>();
    if (!grab || grab.pickOnly || !selected.has(grab.key)) {
      this.clickPress = true;
      this.clickPressKey = grab?.key ?? null;
      this.startMouseX = args.mousePoint.x;
      this.startMouseY = args.mousePoint.y;
      if (grab) {
        // An unselected editable marker never pans or drags the chart — the
        // press can only become a selection click.
        if (this.zoomPan) this.zoomPan.isEnabled = false;
        this.suppressCursor();
        args.handled = true;
      }
      // Empty space / locked markers: panning proceeds normally; a no-move
      // release clears the selection.
      return;
    }
    // A selected marker drags the whole selection (pick-only markers stay put).
    this.pressKey = grab.key;
    const editable = this.editableIdsRef?.current;
    this.dragKeys = [...selected].filter((k) => editable?.has(k));
    this.collectTargets(this.dragKeys);
    if (this.targets.length === 0) return;
    this.dragging = true;
    this.movedPx = false;
    this.startMouseX = args.mousePoint.x;
    this.startMouseY = args.mousePoint.y;
    this.lastDx = 0;
    this.lastDy = 0;
    // Suspend panning + crosshair for the gesture; `handled` stops the pan
    // starting on this mousedown (ZoomPanModifier keeps
    // receiveHandledEvents=false).
    if (this.zoomPan) this.zoomPan.isEnabled = false;
    this.suppressCursor();
    args.handled = true;
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    // A captured press on an unselected marker swallows moves — the chart
    // must not pan and the marker must not move.
    if (this.clickPress && this.clickPressKey) {
      args.handled = true;
      return;
    }
    if (!this.dragging || !this.parentSurface || !this.boundaries) return;
    if (
      Math.abs(args.mousePoint.x - this.startMouseX) >
        CLICK_MOVE_TOLERANCE_PX ||
      Math.abs(args.mousePoint.y - this.startMouseY) > CLICK_MOVE_TOLERANCE_PX
    ) {
      this.movedPx = true;
    }
    const xCalc = this.parentSurface.xAxes
      .get(0)
      .getCurrentCoordinateCalculator();
    const yCalc = this.parentSurface.yAxes
      .getById(TVD_AXIS_ID)
      .getCurrentCoordinateCalculator();
    // Deltas in data space — differencing getDataValue handles the flipped
    // TVD axis and any pixel offset.
    const rawDx =
      xCalc.getDataValue(args.mousePoint.x) -
      xCalc.getDataValue(this.startMouseX);
    const rawDy =
      yCalc.getDataValue(args.mousePoint.y) -
      yCalc.getDataValue(this.startMouseY);
    const snap = (v: number) => Math.round(v / DRAG_SNAP_FT) * DRAG_SNAP_FT;
    let dx = snap(rawDx);
    let dy = snap(rawDy);
    const frozenOf = (key: string) => this.getXFrozen?.(key) ?? false;
    // Shared X clamp: only X-movable targets stay inside the unit boundaries and
    // constrain the delta; X-frozen (permit) targets don't move in X at all.
    let dxLo = -Infinity;
    let dxHi = Infinity;
    let anyXMovable = false;
    for (const t of this.targets) {
      if (frozenOf(t.key)) continue;
      anyXMovable = true;
      dxLo = Math.max(dxLo, this.boundaries.leftXFt - t.baseX);
      dxHi = Math.min(dxHi, this.boundaries.rightXFt - t.baseX);
    }
    dx = anyXMovable ? Math.min(Math.max(dx, dxLo), dxHi) : 0;
    // Shared TVD clamp: LTD targets keep inside their formation (sampled at the
    // post-dx X); permits are exempt (free across formations). If the bounded
    // group is too tall to fit, refuse vertical movement.
    if (this.getTvdBounds) {
      let dyLo = -Infinity;
      let dyHi = Infinity;
      let anyBounded = false;
      for (const t of this.targets) {
        const bounds = this.getTvdBounds(
          t.key,
          t.baseX + (frozenOf(t.key) ? 0 : dx)
        );
        if (!bounds) continue;
        anyBounded = true;
        dyLo = Math.max(dyLo, bounds.lo - t.baseY);
        dyHi = Math.min(dyHi, bounds.hi - t.baseY);
      }
      if (anyBounded)
        dy = dyLo <= dyHi ? Math.min(Math.max(dy, dyLo), dyHi) : 0;
    }
    if (dx === this.lastDx && dy === this.lastDy) {
      args.handled = true;
      return;
    }
    this.lastDx = dx;
    this.lastDy = dy;
    for (const t of this.targets) {
      const newX = t.baseX + (frozenOf(t.key) ? 0 : dx);
      let newY = t.baseY + dy;
      // Final per-target clamp so a sideways drag can't drift an LTD slot out of
      // the dipping interval at its new X; permits (null bounds) stay free.
      const bounds = this.getTvdBounds?.(t.key, newX) ?? null;
      if (bounds) newY = Math.min(Math.max(newY, bounds.lo), bounds.hi);
      (t.series.dataSeries as XyDataSeries).updateXy(t.index, newX, newY);
    }
    for (const a of this.textTargets) {
      a.ann.x1 = a.baseX + dx;
      a.ann.y1 = a.baseY + dy;
    }
    for (const a of this.lineTargets) {
      a.ann.y1 = a.baseY + dy;
      a.ann.labelValue = `${fmtFt(a.baseY + dy)} ft TVD`;
    }
    args.handled = true;
  }

  override modifierMouseUp(args: ModifierMouseArgs): void {
    super.modifierMouseUp(args);
    this.finishGesture(args, true);
  }

  override modifierMouseLeave(args: ModifierMouseArgs): void {
    super.modifierMouseLeave(args);
    this.finishGesture(args, false);
  }

  private finishGesture(args: ModifierMouseArgs, isMouseUp: boolean): void {
    if (this.clickPress) {
      this.clickPress = false;
      const key = this.clickPressKey;
      this.clickPressKey = null;
      const isClick =
        isMouseUp &&
        Math.abs(args.mousePoint.x - this.startMouseX) <=
          CLICK_MOVE_TOLERANCE_PX &&
        Math.abs(args.mousePoint.y - this.startMouseY) <=
          CLICK_MOVE_TOLERANCE_PX;
      const selected = this.selectedKeysRef?.current ?? new Set<string>();
      if (key) {
        // The press was captured (pan suspended) — release it either way.
        if (this.zoomPan) this.zoomPan.isEnabled = true;
        args.handled = true;
        if (isClick) {
          // Click on an unselected marker → select it; a selected pick-only
          // marker (never dragged) toggles off here instead.
          const next = new Set(selected);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          this.onSelectionChange?.(next);
          this.suppressCursor();
        } else {
          // Moved without being a drag — nothing happened; the crosshair
          // returns only when nothing is selected.
          this.restoreCursor(selected.size);
        }
      } else if (isClick && selected.size > 0) {
        // Click on empty space → clear the selection.
        this.onSelectionChange?.(new Set());
        this.restoreCursor(0);
      }
      return;
    }
    if (!this.dragging) return;
    this.dragging = false;
    if (this.zoomPan) this.zoomPan.isEnabled = true;
    let nextSelection: ReadonlySet<string> | null = null;
    if (this.movedPx) {
      if (this.lastDx !== 0 || this.lastDy !== 0) {
        this.onDragEnd?.({
          keys: [...this.dragKeys],
          dxFt: this.lastDx,
          dTvdFt: this.lastDy,
        });
      }
      args.handled = true;
    } else if (isMouseUp) {
      // Click on a selected marker → deselect it. The slot-notes click
      // stays available outside edit mode.
      const selected = this.selectedKeysRef?.current ?? new Set<string>();
      const next = new Set(selected);
      next.delete(this.pressKey);
      nextSelection = next;
      args.handled = true;
    }
    if (nextSelection) this.onSelectionChange?.(nextSelection);
    // Crosshair returns only when nothing ends up selected (the React effect
    // re-asserts this after the state commit).
    const finalSize = nextSelection
      ? nextSelection.size
      : (this.selectedKeysRef?.current.size ?? 0);
    this.restoreCursor(finalSize);
    this.targets = [];
    this.textTargets = [];
    this.lineTargets = [];
  }

  // The cursor modifier is DETACHED rather than disabled — disabling stops
  // it updating but leaves the last-drawn crosshair frozen on screen.
  private suppressCursor(): void {
    if (!this.cursor || !this.parentSurface) return;
    if (this.parentSurface.chartModifiers.asArray().includes(this.cursor)) {
      this.parentSurface.chartModifiers.remove(this.cursor);
    }
  }

  private restoreCursor(finalSelectionSize: number): void {
    if (!this.cursor || !this.parentSurface) return;
    if (
      finalSelectionSize === 0 &&
      !this.parentSurface.chartModifiers.asArray().includes(this.cursor)
    ) {
      this.parentSurface.chartModifiers.add(this.cursor);
    }
  }

  private hitTestGrabbable(
    x: number,
    y: number
  ): {
    key: string;
    series: IRenderableSeries;
    index: number;
    pickOnly: boolean;
  } | null {
    if (!this.parentSurface) return null;
    const editable = this.editableIdsRef?.current ?? new Set<string>();
    const pickable = this.pickableIdsRef?.current ?? new Set<string>();
    if (editable.size === 0 && pickable.size === 0) return null;
    // An editable marker always wins over a pick-only one under the cursor, so
    // a nearby other-formation square never blocks dragging.
    let pickHit: {
      key: string;
      series: IRenderableSeries;
      index: number;
      pickOnly: boolean;
    } | null = null;
    for (const series of this.parentSurface.renderableSeries.asArray()) {
      if (!series.isVisible) continue;
      if (!DRAG_GRAB_PREFIXES.some((p) => series.id.startsWith(p))) continue;
      const provider = series.hitTestProvider;
      if (!provider) continue;
      const ht = provider.hitTest(x, y, this.hitRadius);
      if (
        !ht.isHit ||
        ht.dataSeriesIndex < 0 ||
        !Number.isFinite(ht.distance) ||
        ht.distance > this.hitRadius
      ) {
        continue;
      }
      const ds = series.dataSeries as XyDataSeries;
      const m = ds.getMetadataAt(ht.dataSeriesIndex) as MarkerMeta | null;
      if (!m) continue;
      const slotId = m.slotId ?? null;
      const uwi = m.uwi || null;
      const key =
        slotId !== null ? `slot:${slotId}` : uwi ? `uwi:${uwi}` : null;
      // Not in the editable set (outside the unit boundaries) → not grabbable.
      if (!key) continue;
      if (editable.has(key)) {
        return { key, series, index: ht.dataSeriesIndex, pickOnly: false };
      }
      if (pickHit === null && pickable.has(key)) {
        pickHit = { key, series, index: ht.dataSeriesIndex, pickOnly: true };
      }
    }
    return pickHit;
  }

  // Collect every rendered point belonging to the dragged entities across
  // all draggable + halo + overlay series, matched by metadata
  // (grouping-agnostic), plus their paired annotations.
  private collectTargets(keys: string[]): void {
    this.targets = [];
    this.textTargets = [];
    this.lineTargets = [];
    if (!this.parentSurface) return;
    const slotIds = new Set<number>();
    const uwis = new Set<string>();
    keys.forEach((key) => {
      const parsed = parseEditKey(key);
      if (parsed.slotId !== null) slotIds.add(parsed.slotId);
      if (parsed.uwi) uwis.add(parsed.uwi);
    });
    for (const series of this.parentSurface.renderableSeries.asArray()) {
      if (!series.isVisible) continue;
      if (!DRAG_FOLLOW_PREFIXES.some((p) => series.id.startsWith(p))) continue;
      const ds = series.dataSeries as XyDataSeries;
      const xs = ds.getNativeXValues();
      const ys = ds.getNativeYValues();
      for (let i = 0; i < ds.count(); i++) {
        const m = ds.getMetadataAt(i) as MarkerMeta | null;
        if (!m) continue;
        const matchesSlot =
          m.slotId !== null && m.slotId !== undefined && slotIds.has(m.slotId);
        const matchesUwi = !!m.uwi && uwis.has(m.uwi);
        if (!matchesSlot && !matchesUwi) continue;
        // The entity key this point belongs to — drives its per-well-type clamp.
        const key = matchesSlot ? `slot:${m.slotId}` : `uwi:${m.uwi}`;
        this.targets.push({
          series,
          index: i,
          baseX: xs.get(i),
          baseY: ys.get(i),
          key,
        });
      }
    }
    const anns = this.parentSurface.annotations.asArray();
    // `−` glyphs on not-valued slots travel with their rectangles.
    slotIds.forEach((id) => {
      const ann = anns.find((a) => a.id === `gb-slot-minus-${id}`) as
        TextAnnotation | undefined;
      if (ann) this.textTargets.push({ ann, baseX: ann.x1, baseY: ann.y1 });
    });
    // Dashed TVD guide lines of selected entities follow vertically.
    keys.forEach((key) => {
      const ann = anns.find((a) => a.id === `gb-selected-line-${key}`) as
        HorizontalLineAnnotation | undefined;
      if (ann) this.lineTargets.push({ ann, baseY: ann.y1 });
    });
  }
}

export interface GunBarrelChartProps {
  /** API request mode — e.g. 'single_slice'. Drives slice selection logic. */
  requestMode?: string;
  /** Gun barrel subject (unit boundaries, line extent). */
  subject?: GunBarrelSubject | null;
  /** Formation horizontals (structure grids + TC formations). */
  formationHorizontals?: GunBarrelFormationHorizontals | null;
  /** Wells from the API response (all slices). */
  wells?: GunBarrelWellPoint[];
  /** Slot items from the API response. */
  slots?: GunBarrelSlotItem[];
  /** Whether the data is currently loading. */
  loading?: boolean;
  /** Target spacing (ft) — drives the dashed-blue spacing guide lines. */
  spacingFt?: number | null;
  /** Cross-line setback (ft) — draws shaded keep-out bands at the unit's two
   *  cross-section edges. No band when null/0. */
  crossLineSetbackFt?: number | null;
  /** Per-formation spacing configs (inventory surfaces). When present, the chart
   *  draws EACH formation band's own drainage lines + cross-line setback bands,
   *  bounded to that band's TVD range, instead of the single unit-wide
   *  `spacingFt`/`crossLineSetbackFt`. Joined to bands by `formation` name. */
  formationSpacingConfigs?: {
    formation: string;
    wellSpacingFt: number | null;
    crossLineSetbackFt: number | null;
    drainageSectorCount: number | null;
  }[];
  /** Whether a unit/development area is currently selected. */
  unitSelected?: boolean;
  /**
   * Canvas height. A number is pixels; a string is passed through as a CSS
   * height, so a caller can hand over a `var()`/`calc()` that tracks a resizable
   * container without re-rendering the chart on every drag frame.
   */
  height?: number | string;
  /** Fires when the user clicks a slot rectangle on the chart. */
  onSlotClick?: (slotId: number) => void;
  /** Fires when the user right-clicks any slot marker (any well type) — the
   *  valuation/Notes trigger. Suppresses the native context menu on a hit. */
  onSlotRightClick?: (slotId: number) => void;
  /** Enables drag-to-reposition editing of slot rectangles and wells whose X
   *  offset lies inside the unit boundaries. Markers outside the boundaries
   *  render normally but cannot be grabbed. No-op when no unit is selected or
   *  the subject has no boundaries. */
  editMode?: boolean;
  /** Fires whenever the pending edit set changes — after each completed drag,
   *  and with an empty map when edits are discarded (edit mode off / new
   *  data). Keys are `slot:{slotId}` / `uwi:{uwi}`. */
  onPositionEditsChange?: (
    edits: ReadonlyMap<string, GunBarrelPositionEdit>
  ) => void;
  /** Wells currently passing the Left Nav filters. Those whose UWI matches a
   *  gun barrel well get the Probit-style 3-layer halo (outer glow → white
   *  border → existing dot). Generic optional prop — callers opt in. */
  filteredUwis?: ReadonlyArray<{ uwi: string }>;
  /** When true, well markers vary by well status and the legend items become
   *  interactive toggles. Defaults to false for backward compatibility. */
  statusMarkers?: boolean;
  /**
   * When provided, renders an icon-only image-export trigger (top-right) wired
   * to this chart's SciChart surface. Omit to render no trigger (default).
   */
  exportConfig?: { label: string; filenamePrefix: string };
  /**
   * Externally-driven selection. The Unit Builder scopes slots from its table as
   * well as from the chart, and the mock is explicit that scoping is what gates
   * moving a slot — so the set has to be shared rather than chart-private.
   * Omitted, the chart keeps owning it (existing behaviour).
   */
  selectedKeys?: ReadonlySet<string>;
  /** Fires whenever the selection changes, however it changed. */
  onSelectedKeysChange?: (keys: ReadonlySet<string>) => void;
  /**
   * Publishes the current selection's live position + count so an external
   * control panel (the Unit Builder edit rail) can show it and gate its nudge
   * controls. Fires on selection and on every pending-edit change.
   */
  onSelectionInfoChange?: (info: GunBarrelSelectionInfo) => void;
  /**
   * Draw each slot's / well's name beside its marker. Off by default — at a
   * full unit's slot count the labels overlap badly, so it is opt-in.
   */
  showSlotLabels?: boolean;
  /**
   * Formation NAME to emphasise — the selected type curve's formation on the
   * Inventory Workspace. Its band and left label render highlighted (heavier
   * outline, bold label). Matched case-insensitively against the TC formation
   * name. Omitted → no highlight.
   */
  highlightedFormation?: string | null;
  /** Band-border colour for the highlighted formation. Defaults to the chart's
   *  near-black outline; the Inventory Workspace passes blue. */
  highlightColor?: string;
  /**
   * Apply the Inventory Workspace's per-well-type movement rules: LTD slots are
   * held inside their formation (TVD) and clamped in X to the unit; permits
   * (no-survey wells) move in TVD only, free across formations; drilled (survey)
   * wells are not editable. Off (default) keeps the plain Type Curve behaviour —
   * everything editable, X clamped to the unit, TVD free.
   */
  restrictByWellType?: boolean;
  /**
   * Let LTD slots of the NON-highlighted formations (inside the unit) be
   * clicked into the selection — for Merge Geometries. They are never dragged
   * or nudged; only the highlighted formation stays editable. Off by default.
   */
  crossFormationPicking?: boolean;
}

// Gun Barrel chart: TVD (left) + mirrored TVDSS (right) axes, structure
// formations as colored bands. Markers switch on `unitSelected` — slot-location
// circles before a unit is picked, wells after.
export const GunBarrelChart = forwardRef<
  GunBarrelChartHandle,
  GunBarrelChartProps
>(
  (
    {
      selectedKeys: selectedKeysProp,
      onSelectedKeysChange,
      onSelectionInfoChange,
      showSlotLabels = false,
      requestMode,
      subject,
      formationHorizontals,
      wells = NO_WELLS,
      slots = NO_SLOTS,
      loading = false,
      spacingFt = null,
      crossLineSetbackFt = null,
      formationSpacingConfigs,
      unitSelected = false,
      height = 390,
      onSlotClick,
      onSlotRightClick,
      editMode = false,
      onPositionEditsChange,
      filteredUwis,
      statusMarkers = false,
      exportConfig,
      highlightedFormation,
      highlightColor = '#161616',
      restrictByWellType = false,
      crossFormationPicking = false,
    },
    ref
  ) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const typeCurveTooltipRef = useRef<HTMLDivElement>(null);
    const modifierRef = useRef<GunBarrelHoverModifier | null>(null);
    // New data (a refetch, a unit switch) releases a pinned tooltip. Keyed on
    // the data props only, not the selection: the two clicks of the pinning
    // double-click toggle the selection and must not release it.
    useEffect(() => {
      modifierRef.current?.releasePin();
    }, [slots, wells]);
    const typeCurveContextModifierRef =
      useRef<GunBarrelTypeCurveContextModifier | null>(null);
    const clickModifierRef = useRef<GunBarrelClickModifier | null>(null);
    const onSlotClickRef = useRef(onSlotClick);
    onSlotClickRef.current = onSlotClick;
    const onSlotRightClickRef = useRef(onSlotRightClick);
    onSlotRightClickRef.current = onSlotRightClick;
    const crossLineSetbackRef = useRef(crossLineSetbackFt);
    crossLineSetbackRef.current = crossLineSetbackFt;
    const formationSpacingConfigsRef = useRef(formationSpacingConfigs);
    formationSpacingConfigsRef.current = formationSpacingConfigs;
    const requestModeRef = useRef(requestMode);
    requestModeRef.current = requestMode;
    const subjectRef = useRef(subject);
    subjectRef.current = subject;
    const formationHorizontalsRef = useRef(formationHorizontals);
    formationHorizontalsRef.current = formationHorizontals;
    const wellsRef = useRef(wells);
    wellsRef.current = wells;
    const slotsRef = useRef(slots);
    slotsRef.current = slots;
    const spacingRef = useRef(spacingFt);
    spacingRef.current = spacingFt;
    const unitSelectedRef = useRef(unitSelected);
    unitSelectedRef.current = unitSelected;
    const filteredUwisRef = useRef(filteredUwis);
    filteredUwisRef.current = filteredUwis;
    const statusMarkersRef = useRef(statusMarkers);
    statusMarkersRef.current = statusMarkers;
    // Surface elevation (TVD + TVDSS at a point) — drives the right TVDSS axis
    // labels. Read from the data in renderData; the axis labelProvider closes
    // over this ref since axes are created before data arrives.
    const surfaceElevRef = useRef(0);

    // Statuses hidden by the user via legend toggles. Expired Permit is hidden by
    // default. Only active when `statusMarkers` is true.
    const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(
      () => new Set(['EXPIRED_PERMIT'])
    );
    const hiddenStatusesRef = useRef(hiddenStatuses);
    hiddenStatusesRef.current = hiddenStatuses;

    // ── Edit mode (drag-to-reposition) ─────────────────────────────────────────
    const dragModifierRef = useRef<GunBarrelDragModifier | null>(null);
    const editModeRef = useRef(editMode);
    editModeRef.current = editMode;
    const onPositionEditsChangeRef = useRef(onPositionEditsChange);
    onPositionEditsChangeRef.current = onPositionEditsChange;
    // Pending drag edits keyed `slot:{slotId}` / `uwi:{uwi}` — offsets from the
    // original server positions. renderData re-applies them on every rebuild.
    const [positionEdits, setPositionEdits] = useState<
      Map<string, GunBarrelPositionEdit>
    >(() => new Map());
    const positionEditsRef = useRef(positionEdits);
    positionEditsRef.current = positionEdits;
    // Per-formation band contours (in TVD), keyed by displayColor, published by
    // the render effect so the off-render edit paths (drag modifier + nudge) can
    // clamp an LTD slot inside its formation without rebuilding the bands.
    const bandDataByColorRef = useRef<
      Map<string, { xs: number[]; tops: number[]; bases: number[] }>
    >(new Map());
    // Set for exactly one renderData pass after a drag commit so the rebuild
    // keeps the user's current zoom instead of re-framing the axes.
    const keepRangeOnRebuildRef = useRef(false);
    const cursorModifierRef = useRef<CursorModifier | null>(null);
    const chartSurfaceRef = useRef<SciChartSurface | null>(null);
    // Elements in the editable (selected) state — clicked in edit mode. All
    // selected elements drag together.
    const [internalSelectedKeys, setInternalSelectedKeys] = useState<
      ReadonlySet<string>
    >(() => new Set());
    // Controlled when a set is supplied, internal otherwise, so existing callers
    // are unaffected.
    const selectedKeys = selectedKeysProp ?? internalSelectedKeys;
    const selectedKeysRef = useRef(selectedKeys);
    selectedKeysRef.current = selectedKeys;
    const handleSelectionChange = useCallback(
      (next: ReadonlySet<string>) => {
        keepRangeOnRebuildRef.current = true;
        setInternalSelectedKeys(next);
        onSelectedKeysChange?.(next);
      },
      [onSelectedKeysChange]
    );
    // Discarding the selection has to reach a controlling parent too, or the
    // shared set would keep slots scoped that the chart no longer considers so.
    const clearSelection = useCallback(() => {
      setInternalSelectedKeys((prev) => (prev.size > 0 ? new Set() : prev));
      if (selectedKeysRef.current.size > 0) onSelectedKeysChange?.(new Set());
    }, [onSelectedKeysChange]);
    // The crosshair cursor is removed from the chart while any element is in
    // the editable state — detaching (vs disabling) also clears the drawn
    // crosshair lines and axis labels.
    useEffect(() => {
      const surface = chartSurfaceRef.current;
      const cursor = cursorModifierRef.current;
      if (!surface || !cursor) return;
      const attached = surface.chartModifiers.asArray().includes(cursor);
      if (selectedKeys.size > 0 && attached) {
        surface.chartModifiers.remove(cursor);
      } else if (selectedKeys.size === 0 && !attached) {
        surface.chartModifiers.add(cursor);
      }
    }, [selectedKeys]);

    // Boundary-restricted editable set. Membership keys off the ORIGINAL server
    // positions so an already-moved marker stays grabbable wherever it was
    // dropped. Wells owned by an occupied slot move with their slot instead.
    const editableIds = useMemo(() => {
      const ids = new Set<string>();
      const ub = subject?.unitBoundaries;
      if (!editMode || !unitSelected || !ub || ub.rightXFt <= ub.leftXFt) {
        return ids;
      }
      // Only the highlighted formation is editable when slots are tagged with a
      // formation (the Inventory Workspace flattens several zones into one chart).
      // Untagged slots — the single-formation Type Curve pages — stay editable.
      const hf = highlightedFormation?.toUpperCase() ?? null;
      slots.forEach((s) => {
        if (s.xOffsetFt < ub.leftXFt || s.xOffsetFt > ub.rightXFt) return;
        // A slot in the UNKNOWN bucket belongs to no band, and the builder's
        // stepper never lands on UNKNOWN, so no highlight could reach it. It is
        // editable under any highlight instead: an unlanded permit is dragged
        // down into the band on screen, and lands there.
        if (
          hf !== null &&
          s.formationLabel &&
          !isUnknownZone(s.formationLabel) &&
          s.formationLabel.toUpperCase() !== hf
        ) {
          return;
        }
        // Drilled (survey) wells are not editable in the Inventory Workspace,
        // and a permit is only as editable as the backend says (it must own the
        // slot and know which bore to re-land).
        if (restrictByWellType && classifySlot(s) === 'survey') return;
        if (
          restrictByWellType &&
          classifySlot(s) === 'permit' &&
          s.landingEditable !== true
        ) {
          return;
        }
        // A move still landing is held until it reports, whatever its type.
        if (s.landingPending) return;
        ids.add(`slot:${s.slotId}`);
      });
      // Standalone wells are drilled — excluded from editing under the Inventory
      // Workspace's per-well-type rules; the plain Type Curve page keeps them.
      if (!restrictByWellType) {
        const slotUwis = new Set(
          slots.filter((s) => s.hasWell && s.uwi).map((s) => s.uwi!)
        );
        wells.forEach((w) => {
          if (
            w.uwi &&
            !slotUwis.has(w.uwi) &&
            w.xOffsetFt >= ub.leftXFt &&
            w.xOffsetFt <= ub.rightXFt
          ) {
            ids.add(`uwi:${w.uwi}`);
          }
        });
      }
      return ids;
    }, [
      editMode,
      unitSelected,
      subject,
      slots,
      highlightedFormation,
      wells,
      restrictByWellType,
    ]);
    const editableIdsRef = useRef<ReadonlySet<string>>(editableIds);
    editableIdsRef.current = editableIds;

    const pickableIds = useMemo(() => {
      const ids = new Set<string>();
      const ub = subject?.unitBoundaries;
      if (!crossFormationPicking || !editMode || !unitSelected || !ub) {
        return ids;
      }
      slots.forEach((s) => {
        const key = `slot:${s.slotId}`;
        if (editableIds.has(key)) return;
        if (s.xOffsetFt < ub.leftXFt || s.xOffsetFt > ub.rightXFt) return;
        if (classifySlot(s) !== 'ltd') return;
        ids.add(key);
      });
      return ids;
    }, [
      crossFormationPicking,
      editMode,
      unitSelected,
      subject,
      slots,
      editableIds,
    ]);
    const pickableIdsRef = useRef<ReadonlySet<string>>(pickableIds);
    pickableIdsRef.current = pickableIds;

    // Merge a completed gesture into the cumulative edit map. Gestures report
    // only their own delta — drag bases are read from the live (already-offset)
    // series, so consecutive drags of the same entity must accumulate.
    const handleDragEnd = useCallback((p: DragEndPayload) => {
      keepRangeOnRebuildRef.current = true;
      setPositionEdits((prev) => {
        const next = new Map(prev);
        p.keys.forEach((key) => {
          const cur = prev.get(key);
          // Re-clamp the accumulated delta per key so the committed edit matches
          // the per-well-type constraints (a shared gesture delta over a mixed
          // selection is otherwise wrong for permits / off-formation LTDs).
          const edit = clampEditRef.current(
            key,
            (cur?.dxFt ?? 0) + p.dxFt,
            (cur?.dTvdFt ?? 0) + p.dTvdFt
          );
          if (edit) next.set(key, edit);
        });
        return next;
      });
    }, []);

    // Leaving edit mode discards pending edits + selection; fresh server data
    // invalidates them too. Returning `prev` when already empty avoids a
    // render loop.
    useEffect(() => {
      if (!editMode) {
        setPositionEdits((prev) => (prev.size > 0 ? new Map() : prev));
        clearSelection();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode]);
    useEffect(() => {
      setPositionEdits((prev) => (prev.size > 0 ? new Map() : prev));
      clearSelection();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wells, slots]);
    useEffect(() => {
      onPositionEditsChangeRef.current?.(positionEdits);
    }, [positionEdits]);

    // ── Off-chart editing (edit rail nudge / exact-value) ──────────────────────
    // Resolve an element's ORIGINAL server position + identity from its edit key,
    // using the same TVD basis the markers plot at (surfaceElev − tvdssFt) so the
    // rail's readout matches the chart and the hover tooltip.
    const resolveBase = useCallback(
      (key: string) => {
        const parsed = parseEditKey(key);
        const elev = surfaceElevRef.current;
        if (parsed.kind === 'slot' && parsed.slotId !== null) {
          const s = slotsRef.current.find((it) => it.slotId === parsed.slotId);
          if (!s) return null;
          // Anchored on the persisted depth, matching where the marker is now
          // drawn. It is also what SlotPlacement applies `dtvd_ft` to, so the
          // depth that lands is the depth the operator dragged to — off the band
          // midpoint the two frames disagreed by the midpoint-to-stored offset,
          // and an exact typed value shipped as a delta against the wrong base.
          const tvd = elev - s.tvdssFt;
          return {
            kind: 'slot' as GunBarrelEditTargetKind,
            slotId: s.slotId,
            uwi: s.uwi ?? null,
            x: s.xOffsetFt,
            tvd,
            // Permit (no-survey well) moves in TVD only — the rail disables X.
            isPermit: restrictByWellType && classifySlot(s) === 'permit',
            // Whether that permit may move at all: the backend's per-slot verdict
            // under the Inventory Workspace; unrestricted elsewhere.
            landingEditable: !restrictByWellType || s.landingEditable === true,
          };
        }
        if (parsed.uwi) {
          const w = wellsRef.current.find((it) => it.uwi === parsed.uwi);
          if (!w) return null;
          return {
            kind: 'well' as GunBarrelEditTargetKind,
            slotId: null,
            uwi: w.uwi ?? null,
            x: w.xOffsetFt,
            tvd: elev - w.tvdssFt,
            // Standalone wells are drilled (survey) — not editable, so never a permit.
            isPermit: false,
            landingEditable: true,
          };
        }
        return null;
      },
      [restrictByWellType]
    );

    // ── Per-well-type movement constraints ─────────────────────────────────────
    // A `slot:` key classifies by its slot; a `uwi:` (standalone well) is drilled.
    const classifyKey = useCallback((key: string): GunBarrelItemType => {
      const parsed = parseEditKey(key);
      if (parsed.kind === 'slot' && parsed.slotId !== null) {
        const s = slotsRef.current.find((it) => it.slotId === parsed.slotId);
        return s ? classifySlot(s) : 'survey';
      }
      return 'survey';
    }, []);
    // Permits move in TVD only — their section-line X is frozen.
    const isXFrozen = useCallback(
      (key: string) => restrictByWellType && classifyKey(key) === 'permit',
      [restrictByWellType, classifyKey]
    );
    // Formation top/base (TVD) for an LTD slot at a section-line X, sampled from
    // the slot's formation band so the window follows the dip. Null → no clamp
    // (permit / survey / no band), i.e. free TVD movement across formations.
    const tvdBoundsAtX = useCallback(
      (key: string, xFt: number): { lo: number; hi: number } | null => {
        if (!restrictByWellType || classifyKey(key) !== 'ltd') return null;
        const parsed = parseEditKey(key);
        const s =
          parsed.slotId !== null
            ? slotsRef.current.find((it) => it.slotId === parsed.slotId)
            : undefined;
        const band = s?.color
          ? bandDataByColorRef.current.get(s.color)
          : undefined;
        if (!band || band.xs.length === 0) return null;
        const lo = interpolateAtX(band.xs, band.tops, xFt);
        const hi = interpolateAtX(band.xs, band.bases, xFt);
        return lo <= hi ? { lo, hi } : null;
      },
      [restrictByWellType, classifyKey]
    );

    // Publish the current selection so the edit rail can show the live position
    // and gate its controls. `single` only when exactly one editable element is
    // selected — the case where typing / nudging one value is meaningful.
    const onSelectionInfoChangeRef = useRef(onSelectionInfoChange);
    onSelectionInfoChangeRef.current = onSelectionInfoChange;
    useEffect(() => {
      const cb = onSelectionInfoChangeRef.current;
      if (!cb) return;
      let single: GunBarrelSelectionInfo['single'] = null;
      if (selectedKeys.size === 1) {
        const [key] = [...selectedKeys];
        if (editableIds.has(key)) {
          const base = resolveBase(key);
          if (base) {
            const edit = positionEdits.get(key);
            single = {
              xFt: base.x + (edit?.dxFt ?? 0),
              tvdFt: base.tvd + (edit?.dTvdFt ?? 0),
              isPermit: base.isPermit,
              landingEditable: base.landingEditable,
            };
          }
        }
      }
      // Every selected editable LTD's numeric slot id — the set whose laterals
      // can be trimmed together (one for a single selection, several for a group).
      const ltdSlotIds: number[] = [];
      for (const key of selectedKeys) {
        if (!editableIds.has(key) || classifyKey(key) !== 'ltd') continue;
        const base = resolveBase(key);
        if (base && base.slotId !== null) ltdSlotIds.push(base.slotId);
      }
      cb({ count: selectedKeys.size, single, ltdSlotIds });
    }, [
      selectedKeys,
      editableIds,
      positionEdits,
      slots,
      wells,
      resolveBase,
      classifyKey,
    ]);

    // Clamp a raw (dx, dTvd) offset for one key to its per-well-type boundaries:
    // a permit the backend did not mark landing-editable does not move at all,
    // otherwise its X is frozen; a non-permit's X is clamped to the unit
    // boundaries; an LTD slot's TVD is held inside its formation.
    // Returns the committed edit (or null if the key can't be resolved). Shared
    // by the nudge/exact-value path and the drag-commit path so both agree.
    const clampEdit = useCallback(
      (
        key: string,
        dxFt: number,
        dTvdFt: number
      ): GunBarrelPositionEdit | null => {
        const base = resolveBase(key);
        if (!base) return null;
        // A frozen permit yields no edit at all, so a drag leaves nothing
        // pending and Update Geometries stays unarmed.
        if (base.isPermit && !base.landingEditable) return null;
        const ub = subjectRef.current?.unitBoundaries;
        const snap = (v: number) => Math.round(v / DRAG_SNAP_FT) * DRAG_SNAP_FT;
        let dx = base.isPermit ? 0 : snap(dxFt);
        if (!base.isPermit && ub) {
          // Keep X inside the drainable span — out of the cross-line setback.
          const span = drainableSpan(ub, crossLineSetbackRef.current);
          dx = Math.min(
            Math.max(dx, span.leftXFt - base.x),
            span.rightXFt - base.x
          );
        }
        let dTvd = snap(dTvdFt);
        const bounds = tvdBoundsAtX(key, base.x + dx);
        if (bounds) {
          dTvd =
            Math.min(Math.max(base.tvd + dTvd, bounds.lo), bounds.hi) -
            base.tvd;
        }
        return {
          kind: base.kind,
          slotId: base.slotId,
          uwi: base.uwi,
          dxFt: dx,
          dTvdFt: dTvd,
        };
      },
      [resolveBase, tvdBoundsAtX]
    );
    const clampEditRef = useRef(clampEdit);
    clampEditRef.current = clampEdit;

    // Commit an edit for one key (nudge / exact value), clamped to its
    // boundaries. `compute` derives the new (dx, dTvd) from the key's CURRENT
    // committed edit, read INSIDE the updater (via `prev`) — never from a ref that
    // lags a render. Otherwise two actions batched before the next commit (nudge X
    // then Y, rapid nudges, a drag then an edit) both read the same old delta and
    // the second re-sends a stale value. Mirrors `handleDragEnd`. Preserves zoom.
    const applyEdit = useCallback(
      (
        key: string,
        compute: (cur: GunBarrelPositionEdit | undefined) => {
          dxFt: number;
          dTvdFt: number;
        }
      ) => {
        keepRangeOnRebuildRef.current = true;
        setPositionEdits((prev) => {
          const { dxFt, dTvdFt } = compute(prev.get(key));
          const edit = clampEditRef.current(key, dxFt, dTvdFt);
          if (!edit) return prev;
          return new Map(prev).set(key, edit);
        });
      },
      []
    );

    // The single editable selection, or null — nudge / exact-value act on it only.
    const singleEditableKey = useCallback((): string | null => {
      const sel = selectedKeysRef.current;
      if (sel.size !== 1) return null;
      const [key] = [...sel];
      return editableIdsRef.current.has(key) ? key : null;
    }, []);

    useImperativeHandle(
      ref,
      (): GunBarrelChartHandle => ({
        hideTypeCurveTooltip: () => {
          const el = typeCurveTooltipRef.current;
          if (el) el.style.display = 'none';
        },
        nudgeSelected: (axis: GunBarrelEditAxis, deltaFt: number) => {
          const key = singleEditableKey();
          if (!key) return;
          applyEdit(key, (cur) => {
            if (axis === 'x') {
              return {
                dxFt: (cur?.dxFt ?? 0) + deltaFt,
                dTvdFt: cur?.dTvdFt ?? 0,
              };
            }
            return {
              dxFt: cur?.dxFt ?? 0,
              dTvdFt: (cur?.dTvdFt ?? 0) + deltaFt,
            };
          });
        },
        setSelectedAbsolute: (axis: GunBarrelEditAxis, valueFt: number) => {
          const key = singleEditableKey();
          if (!key || !Number.isFinite(valueFt)) return;
          // `base` is the immutable original server position, so reading it here
          // (outside the updater) is not subject to the render lag.
          const base = resolveBase(key);
          if (!base) return;
          applyEdit(key, (cur) => {
            if (axis === 'x') {
              return { dxFt: valueFt - base.x, dTvdFt: cur?.dTvdFt ?? 0 };
            }
            return { dxFt: cur?.dxFt ?? 0, dTvdFt: valueFt - base.tvd };
          });
        },
        resetEdits: () =>
          setPositionEdits((prev) => (prev.size > 0 ? new Map() : prev)),
      }),
      [applyEdit, resolveBase, singleEditableKey]
    );

    const initChart = ({ surface, wasmContext }: SciChartInitContext) => {
      chartSurfaceRef.current = surface;
      const xLabel = new NumericLabelProvider();
      xLabel.formatLabel = fmtFt;
      xLabel.formatCursorLabel = fmtFt;
      surface.xAxes.add(
        new NumericAxis(wasmContext, {
          axisTitle: 'VS (ft)',
          axisTitleStyle: AXIS_TITLE_STYLE,
          labelStyle: AXIS_LABEL_STYLE,
          autoRange: EAutoRange.Never,
          drawMinorGridLines: false,
          majorGridLineStyle: { color: '#f0f0f0', strokeThickness: 1 },
          labelProvider: xLabel,
        })
      );

      // Left axis = TVD, flipped so deeper (larger TVD) is lower on screen.
      const tvdLabel = new NumericLabelProvider();
      tvdLabel.formatLabel = fmtFt;
      tvdLabel.formatCursorLabel = fmtFt;
      surface.yAxes.add(
        new NumericAxis(wasmContext, {
          id: TVD_AXIS_ID,
          axisTitle: 'TVD (ft)',
          axisTitleStyle: AXIS_TITLE_STYLE,
          labelStyle: AXIS_LABEL_STYLE,
          axisAlignment: EAxisAlignment.Left,
          autoRange: EAutoRange.Never,
          flippedCoordinates: true,
          drawMinorGridLines: false,
          majorGridLineStyle: { color: '#e0e0e0', strokeThickness: 1 },
          labelProvider: tvdLabel,
        })
      );

      // Right axis = TVDSS, a display mirror of the TVD axis (TVDSS = elev − TVD).
      // Shares the TVD coordinate space; only the labels differ, so it stays in
      // sync. No gridlines (the TVD axis owns them) and no bound series.
      const tvdssLabel = new NumericLabelProvider();
      tvdssLabel.formatLabel = (v: number) => fmtFt(surfaceElevRef.current - v);
      tvdssLabel.formatCursorLabel = (v: number) =>
        fmtFt(surfaceElevRef.current - v);
      const tvdssAxis = new NumericAxis(wasmContext, {
        id: TVDSS_AXIS_ID,
        axisTitle: 'TVDSS (ft)',
        axisTitleStyle: AXIS_TITLE_STYLE,
        labelStyle: AXIS_LABEL_STYLE,
        axisAlignment: EAxisAlignment.Right,
        autoRange: EAutoRange.Never,
        flippedCoordinates: true,
        drawMajorGridLines: false,
        drawMinorGridLines: false,
        labelProvider: tvdssLabel,
      });
      surface.yAxes.add(tvdssAxis);

      // Keep the two depth axes locked together on zoom/pan, in both
      // directions — the same two-way mirror the geometry tearsheet's 2D
      // Profile and Cross Section use (`subsurface-chart-axes.ts`).
      const tvdAxis = surface.yAxes.get(0);
      tvdAxis.visibleRangeChanged.subscribe((args) => {
        if (args) tvdssAxis.visibleRange = args.visibleRange;
      });
      // The wheel and axis-drag modifiers move only the axis under the mouse,
      // so a zoom or drag on the TVDSS mirror hands its range back to the TVD
      // axis the series bind to; without this the mirror's labels would move
      // on their own and drift out of step with the plot. The equality guard
      // stops the two echoing each other.
      tvdssAxis.visibleRangeChanged.subscribe((args) => {
        if (args && !args.visibleRange.equals(tvdAxis.visibleRange)) {
          tvdAxis.visibleRange = args.visibleRange;
        }
      });

      // Drag = pan/grab (not rubber-band zoom), matching the mock: wheel zooms,
      // dragging the plot pans, dragging an axis rescales just that axis.
      // The edit-mode drag modifier is added FIRST so it can claim a mousedown
      // on an editable marker before ZoomPanModifier sees the event.
      const drag = new GunBarrelDragModifier();
      const zoomPan = new ZoomPanModifier();
      // Crosshair on hover: dashed X/Y guide lines + axis value labels. The
      // built-in cursor tooltip stays off — GunBarrelHoverModifier owns the
      // marker tooltip. Disabled while any element is in the editable state.
      const cursor = new CursorModifier({
        showTooltip: false,
        showAxisLabels: true,
        crosshairStroke: '#a8a8a8',
        crosshairStrokeThickness: 1,
        crosshairStrokeDashArray: [4, 4],
      });
      cursorModifierRef.current = cursor;
      drag.setRefs({
        zoomPan,
        cursor,
        editModeRef,
        editableIdsRef,
        pickableIdsRef,
        selectedKeysRef,
      });
      surface.chartModifiers.add(drag);
      dragModifierRef.current = drag;
      surface.chartModifiers.add(new MouseWheelZoomModifier());
      surface.chartModifiers.add(zoomPan);
      surface.chartModifiers.add(new XAxisDragModifier());
      surface.chartModifiers.add(new YAxisDragModifier());
      surface.chartModifiers.add(cursor);
      const hover = new GunBarrelHoverModifier();
      hover.receiveHandledEvents = true;
      surface.chartModifiers.add(hover);
      modifierRef.current = hover;

      const typeCurveContext = new GunBarrelTypeCurveContextModifier();
      typeCurveContext.receiveHandledEvents = true;
      surface.chartModifiers.add(typeCurveContext);
      typeCurveContextModifierRef.current = typeCurveContext;

      const click = new GunBarrelClickModifier();
      click.receiveHandledEvents = true;
      surface.chartModifiers.add(click);
      clickModifierRef.current = click;
    };

    const renderData = ({ surface, wasmContext }: SciChartInitContext) => {
      surface.suspendUpdates();
      try {
        // A rebuild right after a drag commit keeps the user's current zoom;
        // every other rebuild re-frames the axes below.
        const keepRange = keepRangeOnRebuildRef.current;
        keepRangeOnRebuildRef.current = false;
        const savedXRange = keepRange
          ? surface.xAxes.get(0).visibleRange
          : null;
        const savedYRange = keepRange
          ? surface.yAxes.get(0).visibleRange
          : null;

        surface.renderableSeries.clear();
        surface.annotations.clear();
        const subject = subjectRef.current;
        if (!subject) return;

        const formationHorizontals = formationHorizontalsRef.current;

        // Wells (all slices) — one of the inputs `resolveSurfaceElevation` reads the
        // datum from, behind the formation grids: ELEV = TVD + TVDSS, constant per
        // well. It answers only when the grids cannot, i.e. when the BE could not
        // resolve `surface_ft` and so sent topTvd/baseTvd null.
        const allWells = wellsRef.current;

        if (!formationHorizontals && allWells.length === 0) return;

        const formations = formationHorizontals?.formations ?? [];
        const typeCurveFormations =
          formationHorizontals?.typeCurveFormations ?? [];
        const ub = subject.unitBoundaries;
        // Mode is driven by unit selection (a UI state) AND a valid unit extent in
        // the response. No unit / no extent → full-opacity bands, no boundary lines.
        const unitSelected =
          unitSelectedRef.current && !!ub && ub.rightXFt > ub.leftXFt;
        // X extent from lineExtent when present, else fall back to the unit
        // boundaries, else a default mile.
        const xMin = subject.lineExtent?.startXFt ?? ub?.leftXFt ?? 0;
        const xMax = subject.lineExtent?.endXFt ?? ub?.rightXFt ?? 5280;
        // Depths that should frame the chart: wells, slots and the type curve's
        // own formation bands.
        const tvdValues: number[] = [];
        // Structure-grid formation lines are context. A collection can carry
        // markers far shallower/deeper than the target zone, and letting them
        // into the frame left hundreds of feet of empty chart. Used only when
        // nothing above exists, so a chart with no wells still gets an axis.
        const contextTvdValues: number[] = [];

        // Assigned unconditionally, not behind an `if`: the ref outlives a dataset, so a
        // guarded write would leave the PREVIOUS unit's elevation in place when the next
        // one cannot supply its own.
        surfaceElevRef.current = resolveSurfaceElevation({
          formations,
          typeCurveFormations,
          slots: slotsRef.current,
          wells: allWells,
        });
        const surfaceElev = surfaceElevRef.current;

        // Pending drag edits — every marker build site below adds the entity's
        // offsets so a rebuild reproduces the moved positions.
        const edits = positionEditsRef.current;
        const slotEdit = (slotId: number) => edits.get(`slot:${slotId}`);
        const wellEdit = (uwi: string | null | undefined) => {
          return uwi ? edits.get(`uwi:${uwi}`) : undefined;
        };

        // Elements in the editable (selected) state — collected while building
        // markers, rendered as rings + dashed TVD guide lines after all series.
        const selKeys = editModeRef.current
          ? selectedKeysRef.current
          : new Set<string>();
        const selectedOverlayPoints: {
          x: number;
          y: number;
          slotId: number | null;
          uwi: string | null;
        }[] = [];

        // Which precomputed slice to read. A drawn cross-section line puts the BE in
        // `single_slice` mode (wells tagged with their true `sliceIndex`); read the
        // bands at that same slice. Multi-slice falls back to the first slice.
        const activeSliceIndex =
          requestModeRef.current === 'single_slice' && allWells.length > 0
            ? allWells[0].sliceIndex
            : SLICE_INDEX;

        // TVD for a grid row: use the explicit TVD grid when present, otherwise
        // derive it from TVDSS via the surface elevation (TVD = ELEV − TVDSS). The BE
        // sends both wherever it resolved `surface_ft`; topTvd/baseTvd are null only
        // where it could not — the same case that leaves `surfaceElev` to a well, or
        // to 0, which is why this derivation still has to exist.
        const tvdRow = (
          tvdGrid: FormationTvdGrid | null,
          tvdssGrid: FormationTvdGrid
        ): (number | null)[] => {
          const explicit = rowAt(tvdGrid, activeSliceIndex);
          if (explicit.length > 0) return explicit;
          return rowAt(tvdssGrid, activeSliceIndex).map((v) => {
            if (v === null) return null;
            return surfaceElev - v;
          });
        };

        // Perpendicular sample-column X positions: from the response when present,
        // otherwise spread evenly across the unit boundaries (the dev-area extent).
        const ncols =
          formations[0]?.topTvdss?.[0]?.length ??
          typeCurveFormations[0]?.topTvdss?.[0]?.length ??
          0;
        const sliceColumnsFt =
          formationHorizontals?.slicePositionsFt ??
          (ub && ncols > 1
            ? Array.from(
                { length: ncols },
                (_, i) =>
                  ub.leftXFt + ((ub.rightXFt - ub.leftXFt) * i) / (ncols - 1)
              )
            : []);

        // Band opacity segments. The outer segments span the full extent so the
        // bands always reach the chart edges; only the in-unit slice is dimmed-out
        // vs full-opacity once a unit is selected.
        const segments =
          unitSelected && ub
            ? [
                {
                  lo: -BAND_X_EXTENT_FT,
                  hi: ub.leftXFt,
                  alpha: BAND_ALPHA_OUTSIDE,
                },
                { lo: ub.leftXFt, hi: ub.rightXFt, alpha: BAND_ALPHA_INSIDE },
                {
                  lo: ub.rightXFt,
                  hi: BAND_X_EXTENT_FT,
                  alpha: BAND_ALPHA_OUTSIDE,
                },
              ]
            : [
                {
                  lo: -BAND_X_EXTENT_FT,
                  hi: BAND_X_EXTENT_FT,
                  alpha: BAND_ALPHA_INSIDE,
                },
              ];

        // Structure-grid formation tops (TVD) — used both to clip the type-curve
        // band fills and to draw the SG top lines below. Skip all-null rows.
        const sgTops = formations
          .map((f) => ({ f, topRow: tvdRow(f.topTvd, f.topTvdss) }))
          .filter(({ topRow }) => topRow.some((v) => v !== null));

        // ── Type-curve formations → filled colored bands (fill = TC color) ────────
        // The fill is the type-curve formation; the structure-grid formations are
        // drawn as lines (below). Band geometry mirrors the well-page chart: the
        // band top tracks the shallowest matching SG top (matched by
        // `typeCurveLabel`), falling back to the TC's own top; the base runs down
        // to the next deeper SG top or next TC band, capped at
        // MAX_FORMATION_THICKNESS_FT. Deeper = larger TVD.
        const sortedTcBands = typeCurveFormations
          .map((tc) => ({
            tc,
            topRow: tvdRow(tc.topTvd, tc.topTvdss),
            baseRow: tvdRow(tc.baseTvd, tc.baseTvdss),
          }))
          .filter(({ topRow }) => topRow.some((v) => v !== null))
          .sort((a, b) => avgDefined(a.topRow) - avgDefined(b.topRow));

        const bandDataByColor = new Map<
          string,
          { xs: number[]; tops: number[]; bases: number[] }
        >();

        // The highlighted formation's contour, outlined last (over the SG lines)
        // so its blue top + base read clearly, as the mock draws them.
        let highlightBand: {
          xs: number[];
          tops: number[];
          bases: number[];
        } | null = null;

        sortedTcBands.forEach(({ tc, topRow, baseRow }, tcIdx) => {
          // Inventory Workspace: emphasise the selected leaf's formation band.
          const isHighlighted =
            !!highlightedFormation &&
            tc.name.toUpperCase() === highlightedFormation.toUpperCase();
          const matching = sgTops.filter(
            ({ f }) => f.typeCurveLabel?.toUpperCase() === tc.name.toUpperCase()
          );
          const matchingRows = matching.map((m) => m.topRow);
          const matchingIds = new Set(matching.map((m) => m.f.gridId));
          const nonMatchingRows = sgTops
            .filter(({ f }) => !matchingIds.has(f.gridId))
            .map((m) => m.topRow);
          const next = sortedTcBands[tcIdx + 1];
          const nextTopRow = next?.topRow ?? [];
          const nextBaseRow = next?.baseRow ?? [];

          const bandTops: (number | null)[] = [];
          const bandBases: (number | null)[] = [];
          for (let i = 0; i < sliceColumnsFt.length; i++) {
            // Band top: shallowest (min TVD) matching SG top, else the TC's own.
            let top: number | null = null;
            for (const row of matchingRows) {
              const v = row[i];
              if (v !== null && v !== undefined && (top === null || v < top)) {
                top = v;
              }
            }
            if (top === null) {
              const t = topRow[i];
              const b = baseRow[i];
              if (t !== null && t !== undefined) top = t;
              if (b !== null && b !== undefined && (top === null || b < top)) {
                top = b;
              }
            }
            if (top === null) {
              bandTops.push(null);
              bandBases.push(null);
              continue;
            }

            // Base: a max-thickness extent, pulled up to the nearest deeper SG top
            // or the next TC band so stacked fills stay disjoint.
            let base = top + MAX_FORMATION_THICKNESS_FT;
            for (const row of nonMatchingRows) {
              const v = row[i];
              if (v === null || v === undefined) continue;
              if (v > top && v < base) base = v;
            }
            if (next) {
              const candidates: number[] = [];
              const tt = nextTopRow[i];
              const tb = nextBaseRow[i];
              if (tt !== null && tt !== undefined) candidates.push(tt);
              if (tb !== null && tb !== undefined) candidates.push(tb);
              if (candidates.length > 0) {
                const nextTop = Math.min(...candidates); // shallowest in TVD
                if (nextTop > top && nextTop < base) base = nextTop;
              }
            }
            if (base - top < 1) {
              bandTops.push(null);
              bandBases.push(null);
              continue;
            }
            bandTops.push(top);
            bandBases.push(base);
          }

          const band = buildBand(
            sliceColumnsFt,
            bandTops,
            bandBases,
            -BAND_X_EXTENT_FT,
            BAND_X_EXTENT_FT
          );
          if (!band) return;
          bandDataByColor.set(tc.displayColor, {
            xs: band.xs,
            tops: band.tops,
            bases: band.bases,
          });
          if (isHighlighted) {
            highlightBand = {
              xs: band.xs,
              tops: band.tops,
              bases: band.bases,
            };
          }
          tvdValues.push(...band.tops);
          segments.forEach((seg, segIdx) => {
            const clipped = clipBand(
              band.xs,
              band.tops,
              band.bases,
              seg.lo,
              seg.hi
            );
            if (clipped.xs.length < 2) return;
            // The highlighted band lifts its fill and gains a dark outline so it
            // reads as selected against the other formations.
            const fill = withAlpha(
              tc.displayColor,
              isHighlighted ? Math.min(seg.alpha + 0.25, 1) : seg.alpha
            );
            surface.renderableSeries.add(
              new FastBandRenderableSeries(wasmContext, {
                id: `gb-tc-${tcIdx}-seg${segIdx}`,
                yAxisId: TVD_AXIS_ID,
                dataSeries: new XyyDataSeries(wasmContext, {
                  xValues: clipped.xs,
                  yValues: clipped.tops,
                  y1Values: clipped.bases,
                }),
                fill,
                fillY1: fill,
                stroke: fill,
                strokeY1: fill,
                // No band outline here — the SG top lines own the edges, and the
                // highlighted formation's blue top/base are drawn last (below) so
                // they sit over those SG lines.
                strokeThickness: 0,
              })
            );
          });
        });
        // Publish the freshly-built bands so the drag/nudge paths can clamp an
        // LTD slot inside its formation (sampled by the slot's displayColor).
        bandDataByColorRef.current = bandDataByColor;

        // ── Structure-grid formation tops → colored lines + right-margin label ────
        // Line color = the structure-grid formation's display color (darkened for
        // contrast). Labels are top-aligned to the line.
        sgTops.forEach(({ f, topRow }) => {
          const xs: number[] = [];
          const ys: number[] = [];
          const n = Math.min(sliceColumnsFt.length, topRow.length);
          for (let i = 0; i < n; i++) {
            const t = topRow[i];
            if (t === null) continue;
            xs.push(sliceColumnsFt[i]);
            ys.push(t);
          }
          if (xs.length === 0) return;
          // Extend to the chart edges (hold the end values) so the line spans the
          // full width like the fills beneath it.
          if (xs[0] > -BAND_X_EXTENT_FT) {
            xs.unshift(-BAND_X_EXTENT_FT);
            ys.unshift(ys[0]);
          }
          if (xs[xs.length - 1] < BAND_X_EXTENT_FT) {
            xs.push(BAND_X_EXTENT_FT);
            ys.push(ys[ys.length - 1]);
          }
          if (xs.length < 2) return;
          contextTvdValues.push(...ys);
          surface.renderableSeries.add(
            new FastLineRenderableSeries(wasmContext, {
              id: `gb-sg-${f.gridId}`,
              yAxisId: TVD_AXIS_ID,
              dataSeries: new XyDataSeries(wasmContext, {
                xValues: xs,
                yValues: ys,
              }),
              stroke: darken(f.displayColor),
              strokeThickness: 1.5,
            })
          );
          // Right margin = structure-formation geologic interval, top-aligned to
          // the formation top line.
          surface.annotations.add(
            new NativeTextAnnotation({
              x1: 1,
              y1: avgDefined(topRow),
              xCoordinateMode: ECoordinateMode.Relative,
              yAxisId: TVD_AXIS_ID,
              text: f.geologicInterval,
              fontSize: 8,
              textColor: '#161616',
              horizontalAnchorPoint: EHorizontalAnchorPoint.Right,
              verticalAnchorPoint: EVerticalAnchorPoint.Top,
            })
          );
        });

        // ── Highlighted formation outline → blue top + base, full width ───────────
        // Drawn after the SG lines so the selected formation's blue edges sit on
        // top and read clearly (the mock's scoped-formation outline).
        if (highlightBand) {
          const outlineBand: {
            xs: number[];
            tops: number[];
            bases: number[];
          } = highlightBand;
          const outlineEdge = (edge: 'tops' | 'bases') =>
            surface.renderableSeries.add(
              new FastLineRenderableSeries(wasmContext, {
                id: `gb-highlight-${edge}`,
                yAxisId: TVD_AXIS_ID,
                dataSeries: new XyDataSeries(wasmContext, {
                  xValues: outlineBand.xs,
                  yValues: outlineBand[edge],
                }),
                stroke: highlightColor,
                // A shade heavier than the mock's 3: at our band heights the
                // scoped formation's top and base need to carry over the SG
                // lines they sit on.
                strokeThickness: 4,
              })
            );
          outlineEdge('tops');
          outlineEdge('bases');
        }

        // ── Dashed-blue spacing guide (drainage sector) lines ─────────────────────
        // Inventory surfaces: draw each formation band's own sector divider lines
        // from that zone's `drainageSectorCount` (N sectors → N-1 dividers), spaced
        // evenly across the unit width and bounded to the band's TVD range.
        // Elsewhere (plain Type Curve page) fall back to a single unit-wide,
        // full-height set from the scalar `spacingFt`.
        const perFormationSpacing = formationSpacingConfigsRef.current;
        if (perFormationSpacing && perFormationSpacing.length > 0 && ub) {
          const spacingByFormation = new Map(
            perFormationSpacing.map((c) => [c.formation.toUpperCase(), c])
          );
          const unitWidth = ub.rightXFt - ub.leftXFt;
          for (const { tc } of sortedTcBands) {
            const cfg = spacingByFormation.get(tc.name.toUpperCase());
            const n = cfg?.drainageSectorCount ?? null;
            // N sectors need N-1 dividers; 0/1 sector or missing count → none.
            if (n === null || n < 2) continue;
            const band = bandDataByColor.get(tc.displayColor);
            if (!band || unitWidth <= 0) continue;
            const adjustedSpacing = unitWidth / n;
            for (let i = 1; i < n; i += 1) {
              const x = ub.leftXFt + i * adjustedSpacing;
              if (x <= ub.leftXFt || x >= ub.rightXFt) continue;
              surface.annotations.add(
                new LineAnnotation({
                  x1: x,
                  x2: x,
                  y1: interpolateAtX(band.xs, band.tops, x),
                  y2: interpolateAtX(band.xs, band.bases, x),
                  yAxisId: TVD_AXIS_ID,
                  stroke: SPACING_LINE_COLOR,
                  strokeThickness: 1,
                  strokeDashArray: [4, 4],
                  isEditable: false,
                })
              );
            }
          }
        } else {
          const spacing = spacingRef.current ?? null;
          if (
            spacing &&
            spacing >= SPACING_LOWER_LIMIT_FT &&
            spacing <= SPACING_UPPER_LIMIT_FT
          ) {
            const addSpacingLine = (x: number) =>
              surface.annotations.add(
                new VerticalLineAnnotation({
                  x1: x,
                  stroke: SPACING_LINE_COLOR,
                  strokeThickness: 1,
                  strokeDashArray: [4, 4],
                  isEditable: false,
                })
              );

            // When unit boundaries are known, distribute n-1 lines evenly where
            // n = floor(unitWidth / spacing).  When n <= 1 the unit is too narrow
            // for even one full spacing interval, so no lines are drawn.
            const unitWidth = ub ? ub.rightXFt - ub.leftXFt : 0;
            if (ub && unitWidth > 0) {
              const n = Math.floor(unitWidth / spacing);
              if (n >= 2) {
                const adjustedSpacing = unitWidth / n;
                for (let i = 1; i < n; i++) {
                  const x = ub.leftXFt + i * adjustedSpacing;
                  if (x > ub.leftXFt && x < ub.rightXFt) addSpacingLine(x);
                }
              }
              // When n <= 1, spacing doesn't subdivide the unit — draw nothing
            } else {
              for (let x = spacing; x <= xMax; x += spacing) addSpacingLine(x);
              for (let x = -spacing; x >= xMin; x -= spacing) addSpacingLine(x);
            }
          }
        }

        // ── Type-curve formations → left name label + TC-id header + slot circles ─
        // Left-margin name (in display color), an optional centered `uniqueTcId`
        // label, and white slot circles shown only before a unit is selected (they
        // give way to the poiLayers wells once one is).
        const typeCurveHitMetas: MarkerMeta[] = [];
        typeCurveFormations.forEach((tc) => {
          const topRow = tvdRow(tc.topTvd, tc.topTvdss);
          const baseRow = tvdRow(tc.baseTvd, tc.baseTvdss);
          let sum = 0;
          let count = 0;
          for (let i = 0; i < topRow.length; i++) {
            const t = topRow[i];
            const b = baseRow[i];
            if (t === null || b === null) continue;
            sum += (t + b) / 2;
            count += 1;
          }
          if (count === 0) return;
          const midTvd = sum / count;
          const topTvd = avgDefined(topRow);

          // Left-margin TC formation name, top-aligned to the formation top.
          //
          // One flat size and colour, per the mock: it calls the scoped formation
          // out with the blue outline on its band rather than by enlarging the
          // label, and we already draw that outline.
          surface.annotations.add(
            new NativeTextAnnotation({
              x1: 0,
              y1: topTvd,
              xCoordinateMode: ECoordinateMode.Relative,
              yAxisId: TVD_AXIS_ID,
              text: tc.name,
              fontSize: 8,
              textColor: '#161616',
              horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
              verticalAnchorPoint: EVerticalAnchorPoint.Top,
            })
          );

          if (tc.uniqueTcId && tc.typeCurveHeaderDetailsId) {
            typeCurveHitMetas.push({
              isSelected: false,
              kind: 'typeCurve' as const,
              label: tc.name,
              tvdFt: midTvd,
              typeCurveHeaderDetailsId: tc.typeCurveHeaderDetailsId,
              tcPublicId: tc.tcPublicId,
              uniqueTcId: tc.uniqueTcId,
              status: tc.status ?? '',
              oilEurMbbl30yr: tc.oilEurMbbl30yr,
              gasEurMmcf30yr: tc.gasEurMmcf30yr,
              waterEurMbbl30yr: tc.waterEurMbbl30yr,
            } as MarkerMeta);
          }

          // Slot locations → white circles at their plotted TVD (no unit selected).
          // The BE's own TVD wins; it is derived from TVDSS only when omitted.
          const slots = (tc.slotLocations ?? []).map((s) => ({
            xFt: s.xFt,
            tvdFt: resolveSlotTvd(s, surfaceElev),
          }));
          if (!unitSelected && slots.length > 0) {
            tvdValues.push(...slots.map((s) => s.tvdFt));
            surface.renderableSeries.add(
              new XyScatterRenderableSeries(wasmContext, {
                id: `gb-slots-${tc.name}`,
                yAxisId: TVD_AXIS_ID,
                dataSeries: new XyDataSeries(wasmContext, {
                  xValues: slots.map((s) => s.xFt),
                  yValues: slots.map((s) => s.tvdFt),
                  metadata: slots.map((s) => ({
                    isSelected: false,
                    kind: 'location' as const,
                    label: tc.name,
                    tvdFt: s.tvdFt,
                  })),
                }),
                pointMarker: new EllipsePointMarker(wasmContext, {
                  width: MARKER_SIZE_PX,
                  height: MARKER_SIZE_PX,
                  fill: '#ffffff',
                  stroke: '#161616',
                  strokeThickness: 2,
                }),
              })
            );
          }
        });

        // ── poiLayers wells (at the current slice) → circle markers ──────────────
        // Shown only once a unit is selected (slot circles stand in before that).
        // SciChart markers are per-series, so wells are grouped into one scatter
        // series per grouping key. In default mode: group by BE `color`. In
        // statusMarkers mode: group by (normalized status, color) so each group
        // gets the right marker shape while preserving the existing fill colors.
        // A slot marker already stands in for the crossing it sits on, so that
        // crossing is dropped to avoid two markers in one place. Match by uwi AND
        // position, not uwi alone: a U-turn well crosses the section more than
        // once and each crossing is its own hit, but the slot covers only one of
        // them — keying on uwi would discard the others.
        const slotXsByUwi = new Map<string, number[]>();
        if (unitSelected) {
          slotsRef.current.forEach((s) => {
            if (!s.hasWell || !s.uwi) return;
            const xs = slotXsByUwi.get(s.uwi);
            if (xs) xs.push(s.xOffsetFt);
            else slotXsByUwi.set(s.uwi, [s.xOffsetFt]);
          });
        }
        const coveredBySlot = (w: GunBarrelWellPoint) => {
          const xs = w.uwi ? slotXsByUwi.get(w.uwi) : undefined;
          if (!xs) return false;
          return xs.some((x) => Math.abs(x - w.xOffsetFt) <= SAME_CROSSING_FT);
        };
        const allSliceWells = unitSelected
          ? allWells.filter(
              (w) => w.sliceIndex === activeSliceIndex && !coveredBySlot(w)
            )
          : [];
        // In statusMarkers mode, skip wells whose status is hidden by the user.
        const wells = statusMarkersRef.current
          ? allSliceWells.filter(
              (w) =>
                !hiddenStatusesRef.current.has((w.status ?? '').toUpperCase())
            )
          : allSliceWells;
        // 3-layer filter halo — runs whenever a unit is selected so it covers
        // both standalone wells AND slot-assigned wells (which are excluded from
        // the `wells` array below but still need halos when filtered).
        if (unitSelected) {
          const filteredUwiSet = new Set(
            (filteredUwisRef.current ?? []).map((w) => w.uwi)
          );

          type HaloPoint = {
            x: number;
            y: number;
            color: string | null;
            uwi: string;
            slotId: number | null;
          };
          const haloPoints: HaloPoint[] = [];

          if (filteredUwiSet.size > 0) {
            // Only show halo if the well is visible (not hidden by status toggle).
            // Standalone wells — `wells` is already filtered by hiddenStatuses
            wells.forEach((w) => {
              if (w.uwi && filteredUwiSet.has(w.uwi)) {
                const e = wellEdit(w.uwi);
                haloPoints.push({
                  x: w.xOffsetFt + (e?.dxFt ?? 0),
                  y: surfaceElev - w.tvdssFt + (e?.dTvdFt ?? 0),
                  color: w.color ?? null,
                  uwi: w.uwi,
                  slotId: null,
                });
              }
            });
            // Slot-assigned wells
            slotsRef.current.forEach((s) => {
              if (
                s.hasWell &&
                s.uwi &&
                filteredUwiSet.has(s.uwi) &&
                !(
                  statusMarkersRef.current &&
                  s.wellStatus &&
                  hiddenStatusesRef.current.has(s.wellStatus.toUpperCase())
                )
              ) {
                // Occupied slot halos follow the slot's edit key.
                const e = slotEdit(s.slotId);
                haloPoints.push({
                  x: s.xOffsetFt + (e?.dxFt ?? 0),
                  y: surfaceElev - s.tvdssFt + (e?.dTvdFt ?? 0),
                  color: s.color ?? null,
                  uwi: s.uwi,
                  slotId: s.slotId,
                });
              }
            });
          }

          if (haloPoints.length > 0) {
            // Layer 1: outer colored glow, one series per color group.
            const haloByColor = new Map<string | null, HaloPoint[]>();
            haloPoints.forEach((p) => {
              const g = haloByColor.get(p.color) ?? [];
              g.push(p);
              haloByColor.set(p.color, g);
            });
            // Halo layers carry an id + uwi metadata so edit-mode drags can
            // move them along with their well (the hover modifier skips them).
            let haloIdx = 0;
            haloByColor.forEach((group, colorKey) => {
              surface.renderableSeries.add(
                new XyScatterRenderableSeries(wasmContext, {
                  id: `gb-halo-glow-${haloIdx++}`,
                  yAxisId: TVD_AXIS_ID,
                  dataSeries: new XyDataSeries(wasmContext, {
                    xValues: group.map((p) => p.x),
                    yValues: group.map((p) => p.y),
                    metadata: group.map((p) => ({
                      isSelected: false,
                      uwi: p.uwi,
                      slotId: p.slotId ?? undefined,
                    })),
                  }),
                  pointMarker: new EllipsePointMarker(wasmContext, {
                    width: 26,
                    height: 26,
                    fill: withAlpha(colorKey ?? SPACING_LINE_COLOR, 0.7),
                    stroke: 'transparent',
                    strokeThickness: 0,
                  }),
                })
              );
            });

            // Layer 2: white border ring — single series for all halo points
            surface.renderableSeries.add(
              new XyScatterRenderableSeries(wasmContext, {
                id: 'gb-halo-ring',
                yAxisId: TVD_AXIS_ID,
                dataSeries: new XyDataSeries(wasmContext, {
                  xValues: haloPoints.map((p) => p.x),
                  yValues: haloPoints.map((p) => p.y),
                  metadata: haloPoints.map((p) => ({
                    isSelected: false,
                    uwi: p.uwi,
                    slotId: p.slotId ?? undefined,
                  })),
                }),
                pointMarker: new EllipsePointMarker(wasmContext, {
                  width: 14,
                  height: 14,
                  fill: '#ffffff',
                  stroke: '#ffffff',
                  strokeThickness: 0,
                }),
              })
            );
          }
        }

        if (wells.length > 0) {
          tvdValues.push(...wells.map((w) => surfaceElev - w.tvdssFt));

          const wellsByKey = new Map<string, GunBarrelWellPoint[]>();
          wells.forEach((w) => {
            const colorKey = w.color ?? '';
            const statusKey = statusMarkersRef.current
              ? (w.status ?? '').toUpperCase()
              : '';
            const key = `${statusKey}__${colorKey}`;
            const group = wellsByKey.get(key);
            if (group) group.push(w);
            else wellsByKey.set(key, [w]);
          });
          let seriesIdx = 0;
          wellsByKey.forEach((group, key) => {
            const colorKey = key.split('__').slice(1).join('__') || null;
            const normStatus = key.split('__')[0] || null;
            const fill = colorKey || '#ffffff';
            const stroke = colorKey ? darken(colorKey) : '#161616';
            const strokeThickness = colorKey ? 1 : 2;
            const markerSize =
              MARKER_SIZE_PX +
              (STATUS_MARKER_SIZE_BONUS[normStatus ?? ''] ?? 0);
            const pointMarker =
              statusMarkersRef.current && normStatus
                ? (
                    STATUS_MARKER_FACTORY[normStatus] ??
                    STATUS_MARKER_FACTORY['PRODUCING']
                  )(wasmContext, markerSize, fill, stroke)
                : new EllipsePointMarker(wasmContext, {
                    width: MARKER_SIZE_PX,
                    height: MARKER_SIZE_PX,
                    fill,
                    stroke,
                    strokeThickness,
                  });
            surface.renderableSeries.add(
              new XyScatterRenderableSeries(wasmContext, {
                id: `${WELL_SERIES_ID}-${seriesIdx++}`,
                yAxisId: TVD_AXIS_ID,
                dataSeries: new XyDataSeries(wasmContext, {
                  xValues: group.map(
                    (w) => w.xOffsetFt + (wellEdit(w.uwi)?.dxFt ?? 0)
                  ),
                  yValues: group.map(
                    (w) =>
                      surfaceElev - w.tvdssFt + (wellEdit(w.uwi)?.dTvdFt ?? 0)
                  ),
                  metadata: group.map((w) => ({
                    isSelected: false,
                    kind: 'well' as const,
                    label: w.uwi14 ?? w.uwi ?? '',
                    tvdFt:
                      surfaceElev - w.tvdssFt + (wellEdit(w.uwi)?.dTvdFt ?? 0),
                    uwi: w.uwi ?? '',
                    wellName: w.wellName ?? null,
                    status: w.status ?? '',
                    typeCurveFormation: w.typeCurveFormation ?? '',
                  })),
                }),
                pointMarker,
              })
            );
          });
        }

        // Standalone wells in the editable (selected) state.
        wells.forEach((w) => {
          if (w.uwi && selKeys.has(`uwi:${w.uwi}`)) {
            const e = wellEdit(w.uwi);
            selectedOverlayPoints.push({
              x: w.xOffsetFt + (e?.dxFt ?? 0),
              y: surfaceElev - w.tvdssFt + (e?.dTvdFt ?? 0),
              slotId: null,
              uwi: w.uwi,
            });
          }
        });

        // ── Slot rectangles (insights_unit_formation_slots) ─────────────────────
        const slotItems = slotsRef.current;

        if (unitSelected && slotItems.length > 0) {
          const slotsWithTvd = slotItems.map((s) => {
            const derivedTvdFt = surfaceElev - s.tvdssFt;
            // Every slot plots at its PERSISTED depth. A well-less LTD used to be
            // re-derived from its formation band's midpoint, which threw away the
            // depth the server had just returned — so a vertical move saved fine
            // and then redrew where it started (X, read straight off the server,
            // always persisted). The tooltip already reported the stored value,
            // so the chart disagreed with itself.
            //
            // Deliberately NOT `resolveSlotTvd`, even though the BE now derives a
            // slot's `tvdFt` from this same TVDSS. For an LTD slot the two agree, so
            // preferring the server value would buy nothing; for a WELL-backed slot
            // `tvdFt` is measured from that well's own kelly bushing while
            // `surfaceElev` is the cross-section's ground datum, so preferring it
            // would split the marker from the halo, the edit rail and the TVDSS axis,
            // which all read `surfaceElev - tvdssFt`. One basis keeps them together.
            const midY = s.tvdssFt;
            return { ...s, tvdFt: derivedTvdFt, backendTvdFt: s.tvdFt, midY };
          });
          // Frame on the slots the user can actually see. The legend's status
          // toggles are applied further down, when each marker's appearance is
          // decided — so an "Expired Permit" slot hidden by default still handed
          // its depth to the axis here, leaving empty chart where its markers
          // would have been. Mirrors the same filter already applied to `wells`.
          tvdValues.push(
            ...slotsWithTvd
              .filter(
                (s) =>
                  !statusMarkersRef.current ||
                  !s.wellStatus ||
                  !hiddenStatusesRef.current.has(s.wellStatus.toUpperCase())
              )
              .map((s) => surfaceElev - s.midY)
          );

          // Group slots by color for separate series
          const slotsByColor = new Map<
            string | null,
            (GunBarrelSlotItem & {
              tvdFt: number;
              backendTvdFt: number | null;
              midY: number;
            })[]
          >();
          slotsWithTvd.forEach((s) => {
            const key = s.color ?? null;
            const group = slotsByColor.get(key) ?? [];
            group.push(
              s as GunBarrelSlotItem & {
                tvdFt: number;
                backendTvdFt: number | null;
                midY: number;
              }
            );
            slotsByColor.set(key, group);
          });

          const hideValuedSlots =
            statusMarkersRef.current &&
            hiddenStatusesRef.current.has('LTD_SLOT');
          const hideUnvaluedSlots =
            statusMarkersRef.current &&
            hiddenStatusesRef.current.has('UNVALUED_LTD_SLOT');

          // Hide the entire slot (square + inner marker) when the well's status
          // is toggled off. Unoccupied slots (hasWell=false) are never filtered.
          const isSlotWellHidden = (s: (typeof slotsWithTvd)[0]) =>
            statusMarkersRef.current &&
            s.hasWell &&
            !!s.wellStatus &&
            hiddenStatusesRef.current.has(s.wellStatus.toUpperCase());

          // Composite wellbores: markers sharing a `compositeId` are one well,
          // linked in drilling order (`compositePosition`). Added before the
          // markers so the link draws underneath them.
          const legsByComposite = new Map<number, typeof slotsWithTvd>();
          slotsWithTvd.forEach((s) => {
            if (s.compositeId === null || s.compositeId === undefined) return;
            if (isSlotWellHidden(s)) return;
            const legs = legsByComposite.get(s.compositeId) ?? [];
            legs.push(s);
            legsByComposite.set(s.compositeId, legs);
          });
          legsByComposite.forEach((legs, compositeId) => {
            if (legs.length < 2) return;
            const ordered = [...legs].sort(
              (a, b) => (a.compositePosition ?? 0) - (b.compositePosition ?? 0)
            );
            surface.renderableSeries.add(
              new FastLineRenderableSeries(wasmContext, {
                id: `gb-composite-${compositeId}`,
                yAxisId: TVD_AXIS_ID,
                dataSeries: new XyDataSeries(wasmContext, {
                  xValues: ordered.map(
                    (l) => l.xOffsetFt + (slotEdit(l.slotId)?.dxFt ?? 0)
                  ),
                  yValues: ordered.map(
                    (l) =>
                      surfaceElev - l.midY + (slotEdit(l.slotId)?.dTvdFt ?? 0)
                  ),
                }),
                stroke: '#005d5d',
                strokeThickness: 3,
                strokeDashArray: [6, 4],
              })
            );
          });

          let slotIdx = 0;
          slotsByColor.forEach((group, colorKey) => {
            // Split into valued and not-valued so they get different marker styles.
            // Also exclude slots whose well's status is toggled off — the entire
            // slot (square + inner marker) hides together.
            const valuedSlots = group.filter(
              (s) => s.valued !== false && !isSlotWellHidden(s)
            );
            const notValuedSlots = group.filter(
              (s) => s.valued === false && !isSlotWellHidden(s)
            );

            const slotMeta = (s: (typeof group)[0]) => {
              // Shift tooltip depths by the pending edit so hover reflects the
              // moved position (TVDSS runs opposite to TVD).
              const dTvd = slotEdit(s.slotId)?.dTvdFt ?? 0;
              return {
                isSelected: false,
                kind: 'slot' as const,
                label: s.uwi ?? `Slot ${s.slotNumber}`,
                // The READOUT reports the server's own TVD; the MARKER plots at
                // `surfaceElev - tvdssFt`. They are different jobs and, for a
                // well-backed slot, different datums: the backend measures that
                // slot's TVD from its well's kelly bushing, while the chart's one
                // shared axis has to put every marker on the section's ground
                // datum. Reporting the axis frame here would print a depth that
                // matches no well record and disagrees with the slot table by the
                // KB height (~24 ft median). TVDSS below is datum-free, so it
                // stays the number to reconcile the two frames against.
                //
                // For an LTD slot the two agree outright — the backend derives its
                // TVD from this same `tvdssFt` — which is why no `hasWell` test is
                // needed to pick between them any more.
                tvdFt: (s.backendTvdFt ?? s.tvdFt) + dTvd,
                tvdssFt: s.tvdssFt - dTvd,
                hasUwi: !!s.uwi,
                slotStatus: s.slotStatus,
                wellStatus: s.wellStatus,
                uwi: s.uwi ?? '',
                wellName: s.wellName ?? null,
                slotId: s.slotId,
                valued: s.valued,
                landingPending: s.landingPending === true,
              };
            };

            // Valued slots — normal color
            if (valuedSlots.length > 0 && !hideValuedSlots) {
              const borderColor = '#161616';
              const fillColor = withAlpha('#ffffff', 0.5);
              surface.renderableSeries.add(
                new XyScatterRenderableSeries(wasmContext, {
                  id: `gb-slot-rect-${slotIdx}`,
                  yAxisId: TVD_AXIS_ID,
                  dataSeries: new XyDataSeries(wasmContext, {
                    xValues: valuedSlots.map(
                      (s) => s.xOffsetFt + (slotEdit(s.slotId)?.dxFt ?? 0)
                    ),
                    yValues: valuedSlots.map(
                      (s) =>
                        surfaceElev - s.midY + (slotEdit(s.slotId)?.dTvdFt ?? 0)
                    ),
                    metadata: valuedSlots.map(slotMeta),
                  }),
                  pointMarker: new SquarePointMarker(wasmContext, {
                    width: MARKER_SIZE_PX + 4,
                    height: MARKER_SIZE_PX + 4,
                    fill: fillColor,
                    stroke: borderColor,
                    strokeThickness: 2,
                  }),
                })
              );
            }

            // Not-valued slots — black rectangle + minus sign centered
            if (notValuedSlots.length > 0 && !hideUnvaluedSlots) {
              surface.renderableSeries.add(
                new XyScatterRenderableSeries(wasmContext, {
                  id: `gb-slot-rect-nv-${slotIdx}`,
                  yAxisId: TVD_AXIS_ID,
                  dataSeries: new XyDataSeries(wasmContext, {
                    xValues: notValuedSlots.map(
                      (s) => s.xOffsetFt + (slotEdit(s.slotId)?.dxFt ?? 0)
                    ),
                    yValues: notValuedSlots.map(
                      (s) =>
                        surfaceElev - s.midY + (slotEdit(s.slotId)?.dTvdFt ?? 0)
                    ),
                    metadata: notValuedSlots.map(slotMeta),
                  }),
                  pointMarker: new SquarePointMarker(wasmContext, {
                    width: MARKER_SIZE_PX + 4,
                    height: MARKER_SIZE_PX + 4,
                    fill: withAlpha('#ffffff', 0.4),
                    stroke: '#161616',
                    strokeThickness: 2,
                  }),
                })
              );

              // Minus sign centered on each not-valued slot. The id lets the
              // edit-mode drag modifier move the glyph with its rectangle.
              notValuedSlots.forEach((s) => {
                const e = slotEdit(s.slotId);
                surface.annotations.add(
                  new TextAnnotation({
                    id: `gb-slot-minus-${s.slotId}`,
                    x1: s.xOffsetFt + (e?.dxFt ?? 0),
                    y1: surfaceElev - s.midY + (e?.dTvdFt ?? 0),
                    yAxisId: TVD_AXIS_ID,
                    yCoordShift: -2,
                    text: '\u2212',
                    fontSize: 14,
                    fontWeight: 'bold',
                    textColor: '#161616',
                    horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
                    verticalAnchorPoint: EVerticalAnchorPoint.Center,
                  })
                );
              });
            }

            // Slot / well name above each marker when labels are on — for EVERY
            // slot in the group (valued and not), not just the unvalued ones.
            // Same text the tooltip uses, so the two never disagree.
            if (showSlotLabels) {
              group.forEach((s) => {
                const e = slotEdit(s.slotId);
                surface.annotations.add(
                  new TextAnnotation({
                    id: `gb-slot-label-${s.slotId}`,
                    x1: s.xOffsetFt + (e?.dxFt ?? 0),
                    y1: surfaceElev - s.midY + (e?.dTvdFt ?? 0),
                    yAxisId: TVD_AXIS_ID,
                    yCoordShift: -(MARKER_SIZE_PX + 8),
                    text: s.uwi ?? `Slot ${s.slotNumber}`,
                    fontSize: 9,
                    // Two tones, as the mock has it: a well's label is muted so
                    // it reads distinctly from the planned "Slot N" labels
                    // (SciChartGunBarrel.tsx:4877-4879). A slot with a uwi IS
                    // the well, so the text and the colour switch together.
                    textColor: s.uwi ? '#525252' : '#161616',
                    horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
                    verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
                  })
                );
              });
            }

            // Circle markers ONLY for slots that have a well, filtered by
            // the hidden-status toggle when statusMarkers mode is active.
            addSlotWellMarkers({
              surface,
              wasmContext,
              group,
              colorKey,
              slotIdx,
              statusMarkers: statusMarkersRef.current,
              hiddenStatuses: hiddenStatusesRef.current,
              slotEdit,
              surfaceElev,
            });

            slotIdx++;
          });

          // Slots in the editable (selected) state (hidden slots excluded).
          slotsWithTvd.forEach((s) => {
            if (selKeys.has(`slot:${s.slotId}`) && !isSlotWellHidden(s)) {
              const e = slotEdit(s.slotId);
              selectedOverlayPoints.push({
                x: s.xOffsetFt + (e?.dxFt ?? 0),
                y: surfaceElev - s.midY + (e?.dTvdFt ?? 0),
                slotId: s.slotId,
                uwi: s.uwi ?? null,
              });
            }
          });
        }

        // ── Editable-state overlay: blue ring + dashed TVD guide line with an
        // axis label per selected element (drag moves them with the markers) ──
        if (selectedOverlayPoints.length > 0) {
          surface.renderableSeries.add(
            new XyScatterRenderableSeries(wasmContext, {
              id: 'gb-selected-ring',
              yAxisId: TVD_AXIS_ID,
              dataSeries: new XyDataSeries(wasmContext, {
                xValues: selectedOverlayPoints.map((p) => p.x),
                yValues: selectedOverlayPoints.map((p) => p.y),
                metadata: selectedOverlayPoints.map((p) => ({
                  isSelected: false,
                  slotId: p.slotId ?? undefined,
                  uwi: p.uwi ?? '',
                })),
              }),
              // Solid blue dot slightly larger than the marker it covers — the
              // selected element reads as "turned blue".
              pointMarker: new EllipsePointMarker(wasmContext, {
                width: MARKER_SIZE_PX + 6,
                height: MARKER_SIZE_PX + 6,
                fill: SPACING_LINE_COLOR,
                stroke: SPACING_LINE_COLOR,
                strokeThickness: 0,
              }),
            })
          );
          selectedOverlayPoints.forEach((p) => {
            const key = p.slotId !== null ? `slot:${p.slotId}` : `uwi:${p.uwi}`;
            surface.annotations.add(
              new HorizontalLineAnnotation({
                id: `gb-selected-line-${key}`,
                y1: p.y,
                yAxisId: TVD_AXIS_ID,
                stroke: SPACING_LINE_COLOR,
                strokeThickness: 1,
                strokeDashArray: [4, 4],
                showLabel: true,
                labelPlacement: ELabelPlacement.Axis,
                labelValue: `${fmtFt(p.y)} ft TVD`,
                axisLabelFill: SPACING_LINE_COLOR,
                axisLabelStroke: '#ffffff',
                isEditable: false,
              })
            );
          });
        }

        // ── Unit boundary lines (only when a unit is selected; left edge at x=0) ──
        if (unitSelected && ub) {
          [ub.leftXFt, ub.rightXFt].forEach((x) =>
            surface.annotations.add(
              new VerticalLineAnnotation({
                x1: x,
                stroke: UNIT_BOUNDARY_COLOR,
                strokeThickness: 2,
                isEditable: false,
              })
            )
          );

          // ── Cross-line setback: shaded keep-out bands at both unit edges ──
          // Inventory surfaces: each formation band's keep-out uses that zone's
          // cross-line setback, bounded to the band's TVD range. Elsewhere fall
          // back to a single full-height band (relative Y) from the scalar. Width
          // is clamped to half the unit so the two edge bands never overlap; feet
          // map 1:1 to the VS axis.
          const perFormationSetback = formationSpacingConfigsRef.current;
          if (perFormationSetback && perFormationSetback.length > 0) {
            const setbackByFormation = new Map(
              perFormationSetback.map((c) => [c.formation.toUpperCase(), c])
            );
            for (const { tc } of sortedTcBands) {
              const cfg = setbackByFormation.get(tc.name.toUpperCase());
              const setback = cfg?.crossLineSetbackFt ?? 0;
              if (setback <= 0) continue;
              const band = bandDataByColor.get(tc.displayColor);
              if (!band) continue;
              const b = Math.min(setback, (ub.rightXFt - ub.leftXFt) / 2);
              [
                [ub.leftXFt, ub.leftXFt + b],
                [ub.rightXFt - b, ub.rightXFt],
              ].forEach(([x1, x2]) => {
                const midX = (x1 + x2) / 2;
                surface.annotations.add(
                  new BoxAnnotation({
                    x1,
                    x2,
                    y1: interpolateAtX(band.xs, band.tops, midX),
                    y2: interpolateAtX(band.xs, band.bases, midX),
                    yAxisId: TVD_AXIS_ID,
                    fill: SETBACK_BAND_FILL,
                    strokeThickness: 0,
                    isEditable: false,
                  })
                );
              });
            }
          } else {
            const setback = crossLineSetbackFt ?? 0;
            if (setback > 0) {
              const b = Math.min(setback, (ub.rightXFt - ub.leftXFt) / 2);
              [
                [ub.leftXFt, ub.leftXFt + b],
                [ub.rightXFt - b, ub.rightXFt],
              ].forEach(([x1, x2]) =>
                surface.annotations.add(
                  new BoxAnnotation({
                    x1,
                    x2,
                    y1: 0,
                    y2: 1,
                    yCoordinateMode: ECoordinateMode.Relative,
                    fill: SETBACK_BAND_FILL,
                    strokeThickness: 0,
                    isEditable: false,
                  })
                )
              );
            }
          }
        }

        // ── Axes ranges (TVD primary; TVDSS mirror follows via the subscription) ──
        surface.xAxes.get(0).visibleRange =
          savedXRange ?? new NumberRange(xMin, xMax);
        let typeCurveHitBands: TypeCurveHitBand[] = [];
        const framingValues =
          tvdValues.length > 0 ? tvdValues : contextTvdValues;
        if (framingValues.length > 0) {
          const top = Math.min(...framingValues);
          const bottom = Math.max(...framingValues);
          const pad = Math.max(100, (bottom - top) * 0.1);
          const range = savedYRange ?? new NumberRange(top - pad, bottom + pad);
          surface.yAxes.get(0).visibleRange = range;
          const tvdss = surface.yAxes.getById(TVDSS_AXIS_ID);
          if (tvdss) tvdss.visibleRange = range;

          // calculate hit bands for each type curve
          const sortedTypeCurves = typeCurveHitMetas
            .slice()
            .sort((a, b) => a.tvdFt - b.tvdFt);
          typeCurveHitBands = sortedTypeCurves.map((meta, idx) => {
            const prev = sortedTypeCurves[idx - 1];
            const next = sortedTypeCurves[idx + 1];
            return {
              meta,
              yMinTvd: prev ? (prev.tvdFt + meta.tvdFt) / 2 : range.min,
              yMaxTvd: next ? (meta.tvdFt + next.tvdFt) / 2 : range.max,
            };
          });
        }

        modifierRef.current?.setConfig({
          tooltipEl: tooltipRef.current,
          spacingFt: spacingRef.current ?? 0,
          elevFt: surfaceElevRef.current,
        });

        typeCurveContextModifierRef.current?.setConfig({
          tooltipEl: typeCurveTooltipRef.current,
          hitBands: typeCurveHitBands,
        });

        clickModifierRef.current?.setCallback(onSlotClickRef.current ?? null);
        clickModifierRef.current?.setRightClickCallback(
          onSlotRightClickRef.current ?? null
        );

        // Null boundaries fully disables edit dragging (no unit / no extent).
        // Boundaries are the drainable span, so a drag can't enter the cross-line
        // setback keep-out bands.
        dragModifierRef.current?.setConfig({
          boundaries:
            unitSelected && ub ? drainableSpan(ub, crossLineSetbackFt) : null,
          onDragEnd: handleDragEnd,
          onSelectionChange: handleSelectionChange,
          getXFrozen: isXFrozen,
          getTvdBounds: tvdBoundsAtX,
        });
      } finally {
        surface.resumeUpdates();
      }
    };

    const hasWells = wells.length > 0;
    const hasSlots = slots.length > 0;
    const hasUnvaluedSlots = slots.some((s) => s.valued === false);

    const toggleStatus = (status: string) => {
      setHiddenStatuses((prev) => {
        const next = new Set(prev);
        if (next.has(status)) next.delete(status);
        else next.add(status);
        return next;
      });
    };

    return (
      <>
        <SciChartView
          initChart={initChart}
          renderData={renderData}
          renderDeps={[
            requestMode,
            subject,
            formationHorizontals,
            wells,
            slots,
            spacingFt,
            crossLineSetbackFt,
            formationSpacingConfigs,
            unitSelected,
            onSlotClick,
            onSlotRightClick,
            filteredUwis,
            positionEdits,
            selectedKeys,
            highlightedFormation,
            highlightColor,
            showSlotLabels,
            ...(statusMarkers ? [hiddenStatuses] : []),
          ]}
          isLoading={loading}
          height={height}
          exportConfig={exportConfig}
        >
          <div ref={tooltipRef} className={styles.tooltip} />
          <div ref={typeCurveTooltipRef} className={styles.contextTooltip} />
        </SciChartView>
        {unitSelected && (hasWells || hasSlots) && (
          <div className={styles.legend}>
            <span className={styles.legendPill}>
              {statusMarkers ? (
                <>
                  {Object.entries(STATUS_LABEL).map(([status, label]) => {
                    const exists = wells.some(
                      (w) => (w.status ?? '').toUpperCase() === status
                    );
                    if (!exists) return null;
                    const isHidden = hiddenStatuses.has(status);
                    return (
                      <button
                        key={status}
                        type="button"
                        className={`${styles.legendItem} ${
                          styles.legendItemClickable
                        }${isHidden ? ` ${styles.legendItemHidden}` : ''}`}
                        onClick={() => toggleStatus(status)}
                      >
                        <span
                          className={
                            styles[STATUS_ICON_CLASS[status] ?? 'legendCircle']
                          }
                        />
                        {label}
                      </button>
                    );
                  })}
                  {/* The four Create New Well statuses: one entry and toggle each, a hollow
                      hexagon in the status's own colour (INS-13300). */}
                  {WELL_STATUS_SLOT_CATEGORY_KEYS.map((status) => {
                    const key = status.toUpperCase();
                    if (
                      !wells.some((w) => (w.status ?? '').toUpperCase() === key)
                    ) {
                      return null;
                    }
                    return (
                      <button
                        key={key}
                        type="button"
                        className={classNames(
                          styles.legendItem,
                          styles.legendItemClickable,
                          { [styles.legendItemHidden]: hiddenStatuses.has(key) }
                        )}
                        onClick={() => toggleStatus(key)}
                      >
                        <svg
                          className={styles.legendHexagon}
                          viewBox="0 0 12 12"
                          aria-hidden="true"
                        >
                          <polygon
                            data-testid={`legend-hexagon-${status}`}
                            points="6,1 10.33,3.5 10.33,8.5 6,11 1.67,8.5 1.67,3.5"
                            fill="none"
                            stroke={WELL_STATUS_COLOR_MAP[status]}
                            strokeWidth={1.5}
                          />
                        </svg>
                        {SLOT_CATEGORY_LABELS[status]}
                      </button>
                    );
                  })}
                  {hasSlots && (
                    <button
                      type="button"
                      className={`${styles.legendItem} ${
                        styles.legendItemClickable
                      }${
                        hiddenStatuses.has('LTD_SLOT')
                          ? ` ${styles.legendItemHidden}`
                          : ''
                      }`}
                      onClick={() => toggleStatus('LTD_SLOT')}
                    >
                      <span className={styles.legendSquare} />
                      LTD Slot
                    </button>
                  )}
                  {hasUnvaluedSlots && (
                    <button
                      type="button"
                      className={`${styles.legendItem} ${
                        styles.legendItemClickable
                      }${
                        hiddenStatuses.has('UNVALUED_LTD_SLOT')
                          ? ` ${styles.legendItemHidden}`
                          : ''
                      }`}
                      onClick={() => toggleStatus('UNVALUED_LTD_SLOT')}
                    >
                      <span className={styles.legendSquareMinus} />
                      Unvalued LTD Slot
                    </button>
                  )}
                </>
              ) : (
                <>
                  {hasWells && (
                    <span className={styles.legendItem}>
                      <span className={styles.legendCircle} /> Producing wells
                    </span>
                  )}
                  {hasSlots && (
                    <span className={styles.legendItem}>
                      <span className={styles.legendSquare} /> LTDs
                    </span>
                  )}
                </>
              )}
            </span>
          </div>
        )}
      </>
    );
  }
);

GunBarrelChart.displayName = 'GunBarrelChart';
